/**
 * OutreachPage.jsx — Unified outreach command center
 *
 * Single merged view:
 *   - Compact stats bar (streak, XP, level)
 *   - Mission queue (enriched cards with research intel)
 *   - Full prospect table with batch activation
 *   - Templates accessible via dialog
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip, IconButton, Button,
  TextField, Select, MenuItem, Menu, FormControl, InputLabel, Dialog,
  DialogTitle, DialogContent, DialogActions, Tooltip, LinearProgress,
  Card, CardContent, Stack, InputAdornment, Snackbar, Alert,
  ToggleButton, ToggleButtonGroup, Checkbox, FormControlLabel, Collapse,
  CircularProgress, Divider, Autocomplete, Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  ContentCopy as CopyIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Textsms as TextIcon,
  Instagram as InstagramIcon,
  CheckCircle as CheckIcon,
  LocalFireDepartment as FireIcon,
  EmojiEvents as TrophyIcon,
  PlayArrow as PlayIcon,
  Flag as FlagIcon,
  Star as StarIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  AutoAwesome as AIIcon,
  Refresh as RefreshIcon,
  Language as WebIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  Videocam as VideoIcon,
  Campaign as OutreachIcon,
  Link as LinkIcon,
  Undo as UndoIcon,
  CheckCircleOutline as AcceptIcon,
  Reply as ReplyIcon,
  SaveAlt as SaveIcon,
  Event as CalendarIcon,
  OpenInNew as OpenIcon,
  Description as TemplateIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Snooze as SnoozeIcon,
  Block as BlockIcon,
  PowerSettingsNew as DeactivateIcon,
} from '@mui/icons-material';

import { useContacts, useUpdateContact } from '../hooks/useContacts';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryClient';
import { useOutreachActions, useLogOutreachAction } from '../hooks/useOutreach';
import { useProfiles } from '../hooks/useProfiles';
import { useAuth } from '../hooks/useAuth';
import { useMobile } from '../hooks/useMobile';
import { useSettings, useUpdateSettings } from '../hooks/useSettings';
import {
  CADENCE_STEPS, XP_TABLE, OUTREACH_STATUSES, OUTREACH_STATUS_COLORS,
  SEGMENTS, SEGMENT_LABELS, CHANNELS, CHANNEL_LABELS,
  calculateTotalXP, getLevelForXP, computeStreak, getCadenceState,
  getTodaysMissions, getUserStats, getXP, getUnstartedByBrokerage, getNextStatus,
  getCadenceForSegment, classifyAgent,
  NATURES, NATURE_LABELS, TEMPLATE_CHANNELS, NOTE_CHANNELS,
  getNaturesForChannel, getNatureFromStep, getExperience, formatExperience,
} from '../lib/outreachEngine';
import {
  TEMPLATES, TEMPLATE_LIST, fillTemplate, getProspectVars, getTemplateForStep,
} from '../lib/outreachTemplates';
import AgentAvatar from '../components/AgentAvatar';
import { api } from '../lib/api';

// ── Helpers ─────────────────────────────────────────────────
function cleanCopy(text) {
  return text
    .replace(/\{\{[^}]+\}\}/g, '')
    .replace(/--+/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Convert plain text to minimal HTML paragraphs — no font / style attributes */
function textToHtml(text) {
  const cleaned = cleanCopy(text);
  return cleaned
    .split(/\n{2,}/)
    .map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/** Write both text/plain and text/html to clipboard (HTML has structure, no styles) */
async function richCopy(text) {
  const plain = cleanCopy(text);
  const html = textToHtml(text);
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([plain], { type: 'text/plain' }),
        'text/html':  new Blob([html],  { type: 'text/html' }),
      }),
    ]);
  } catch {
    // Fallback for browsers that don't support ClipboardItem
    await navigator.clipboard.writeText(plain);
  }
}

function fmtDate(d) {
  if (!d) return '—';
  const s = String(d);
  const dateOnly = s.includes('T') ? s.split('T')[0] : s;
  return new Date(dateOnly + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtLicensed(exp) {
  return exp.exactDate
    ? exp.issued.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : String(exp.startYear);
}

function experienceTooltip(exp) {
  return [
    `Licensed ${fmtLicensed(exp)}`,
    exp.licenseId && `DRE #${exp.licenseId}`,
    exp.lowerBound && 'earliest record; may be earlier',
  ].filter(Boolean).join(' · ');
}

function StatusChip({ status }) {
  return (
    <Chip
      label={status?.replace(/_/g, ' ') || 'new'}
      size="small"
      sx={{
        bgcolor: OUTREACH_STATUS_COLORS[status] || '#94A3B8',
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.7rem',
        textTransform: 'capitalize',
      }}
    />
  );
}

function SegmentChip({ segment }) {
  const colors = { top_producer: '#EF4444', luxury: '#A855F7', mid_volume: '#3B82F6', new_agent: '#22C55E', unknown: '#94A3B8' };
  return (
    <Chip
      label={SEGMENT_LABELS[segment] || 'Unclassified'}
      size="small"
      variant="outlined"
      sx={{ borderColor: colors[segment] || '#94A3B8', color: colors[segment] || '#94A3B8', fontSize: '0.65rem' }}
    />
  );
}

const CHANNEL_ICONS = {
  email: EmailIcon,
  text: TextIcon,
  phone: PhoneIcon,
  instagram_dm: InstagramIcon,
  social: InstagramIcon,
  in_person: PersonIcon,
};

// ═══════════════════════════════════════════════════════════
// MAIN PAGE — Unified View
// ═══════════════════════════════════════════════════════════
export default function OutreachPage() {
  const { user } = useAuth();
  const { isMobile } = useMobile();
  const { data: settings } = useSettings();
  const { data: allContacts = [] } = useContacts();
  const { data: allActions = [] } = useOutreachActions();
  const { data: allProfiles = [] } = useProfiles();
  const logAction = useLogOutreachAction();
  const updateContact = useUpdateContact();
  const queryClient = useQueryClient();

  const [snack, setSnack] = useState(null);
  const [selectedProspectId, setSelectedProspectId] = useState(null);
  const [actionDialog, setActionDialog] = useState(null);
  const [responseDialog, setResponseDialog] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [outreachSettingsOpen, setOutreachSettingsOpen] = useState(false);
  const [dismissDialog, setDismissDialog] = useState(null);
  const [missionsExpanded, setMissionsExpanded] = useState(true);
  const [focusChannel, setFocusChannel] = useState(null); // null = follow cadence
  const [activityLogExpanded, setActivityLogExpanded] = useState(false);
  const [expandedLogDates, setExpandedLogDates] = useState(new Set());
  const [lastCompletedProspectId, setLastCompletedProspectId] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [officeFilter, setOfficeFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [listMode, setListMode] = useState('prospects');

  // Batch activation
  const [selectedForBatch, setSelectedForBatch] = useState(new Set());
  const [activating, setActivating] = useState(false);
  const [activateProgress, setActivateProgress] = useState('');

  // Derived data
  const prospects = useMemo(() => listMode === 'prospects'
    ? allContacts.filter(c => c.type === 'prospect' || (c.type === 'client' && c.data?.segment))
    : allContacts, [allContacts, listMode]);
  const experienceById = useMemo(() => {
    const now = new Date();
    return new Map(prospects.map(p => [p.id, getExperience(p.data, now)]));
  }, [prospects]);
  const selectedProspect = useMemo(() => selectedProspectId ? prospects.find(p => p.id === selectedProspectId) || null : null, [selectedProspectId, prospects]);

  const actionsByContact = useMemo(() => {
    const map = new Map();
    for (const a of allActions) {
      if (!map.has(a.contactId)) map.set(a.contactId, []);
      map.get(a.contactId).push(a);
    }
    for (const [, arr] of map) arr.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    return map;
  }, [allActions]);

  const stats = useMemo(() => getUserStats(allActions, user?.id), [allActions, user?.id]);
  const missions = useMemo(() => getTodaysMissions(prospects, actionsByContact), [prospects, actionsByContact]);

  // User map: userId → { fullName, accentColor }
  const userMap = useMemo(() => {
    const m = new Map();
    for (const p of allProfiles) m.set(p.id, { fullName: p.fullName || p.email, accentColor: p.accentColor });
    return m;
  }, [allProfiles]);

  // Prospect owners: contactId → Set of user IDs who've done outreach
  const prospectOwners = useMemo(() => {
    const m = new Map();
    for (const a of allActions) {
      if (!a.userId) continue;
      if (!m.has(a.contactId)) m.set(a.contactId, new Set());
      m.get(a.contactId).add(a.userId);
    }
    return m;
  }, [allActions]);

  // Activity Log — group all actions by date (descending), then by prospect
  const activityLog = useMemo(() => {
    const byDate = {};
    const _now = new Date();
    const todayDs = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;
    for (const a of allActions) {
      const ac = a.createdAt ? new Date(a.createdAt) : _now;
      const ds = `${ac.getFullYear()}-${String(ac.getMonth() + 1).padStart(2, '0')}-${String(ac.getDate()).padStart(2, '0')}`;
      if (!byDate[ds]) byDate[ds] = [];
      byDate[ds].push(a);
    }
    const prospectMap = new Map(prospects.map(p => [p.id, p]));
    const days = Object.keys(byDate).sort((a, b) => b.localeCompare(a)).slice(0, 30);
    return days.map(ds => {
      const actions = byDate[ds];
      const byProspect = new Map();
      for (const a of actions) {
        if (!byProspect.has(a.contactId)) byProspect.set(a.contactId, []);
        byProspect.get(a.contactId).push(a);
      }
      const prospectRows = [...byProspect.entries()].map(([cid, acts]) => ({
        prospect: prospectMap.get(cid) || { id: cid, name: 'Unknown' },
        actions: acts,
        channels: [...new Set(acts.map(a => a.channel).filter(Boolean))],
      }));
      return { date: ds, totalActions: actions.length, prospects: prospectRows, isToday: ds === todayDs };
    });
  }, [allActions, prospects]);
  const totalLogActions = useMemo(() => allActions.length, [allActions]);

  // Filter unique values
  const offices = useMemo(() => [...new Set(prospects.map(p => p.company).filter(Boolean))].sort(), [prospects]);
  const teams = useMemo(() => [...new Set(prospects.map(p => p.data?.team).filter(Boolean))].sort(), [prospects]);

  // Counts per filter category
  const segmentCounts = useMemo(() => {
    const m = {};
    for (const p of prospects) m[p.data?.segment || 'unknown'] = (m[p.data?.segment || 'unknown'] || 0) + 1;
    return m;
  }, [prospects]);
  const statusCounts = useMemo(() => {
    const m = {};
    for (const p of prospects) {
      const s = p.data?.outreach_status || p.data?.outreachStatus || 'new';
      m[s] = (m[s] || 0) + 1;
    }
    return m;
  }, [prospects]);
  const officeCounts = useMemo(() => {
    const m = {};
    for (const p of prospects) if (p.company) m[p.company] = (m[p.company] || 0) + 1;
    return m;
  }, [prospects]);
  const teamCounts = useMemo(() => {
    const m = { _none: 0 };
    for (const p of prospects) {
      const t = p.data?.team;
      if (t) m[t] = (m[t] || 0) + 1;
      else m._none++;
    }
    return m;
  }, [prospects]);

  // Filtered & sorted prospects
  const filtered = useMemo(() => {
    let list = [...prospects];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.email || '').toLowerCase().includes(q) ||
        (p.company || '').toLowerCase().includes(q) ||
        (p.data?.team || '').toLowerCase().includes(q)
      );
    }
    if (segmentFilter !== 'all') list = list.filter(p => (p.data?.segment || 'unknown') === segmentFilter);
    if (statusFilter !== 'all') list = list.filter(p => (p.data?.outreach_status || p.data?.outreachStatus || 'new') === statusFilter);
    if (officeFilter !== 'all') list = list.filter(p => p.company === officeFilter);
    if (teamFilter !== 'all') {
      if (teamFilter === '_none') list = list.filter(p => !p.data?.team);
      else list = list.filter(p => p.data?.team === teamFilter);
    }
    list.sort((a, b) => {
      let va, vb;
      if (sortField === 'name') { va = a.name || ''; vb = b.name || ''; }
      else if (sortField === 'office') { va = a.company || ''; vb = b.company || ''; }
      else if (sortField === 'status') { va = a.data?.outreach_status || 'new'; vb = b.data?.outreach_status || 'new'; }
      else if (sortField === 'actions') {
        va = (actionsByContact.get(a.id) || []).length;
        vb = (actionsByContact.get(b.id) || []).length;
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      else if (sortField === 'experience') {
        const ea = experienceById.get(a.id);
        const eb = experienceById.get(b.id);
        if (!ea || !eb) {
          if (ea || eb) return ea ? -1 : 1;
          return (a.name || '').localeCompare(b.name || '');
        }
        const diff = sortDir === 'asc' ? ea.years - eb.years : eb.years - ea.years;
        return diff || (a.name || '').localeCompare(b.name || '');
      }
      else { va = a.name || ''; vb = b.name || ''; }
      const cmp = String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [prospects, search, segmentFilter, statusFilter, officeFilter, teamFilter, sortField, sortDir, actionsByContact, experienceById]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir(field === 'experience' ? 'desc' : 'asc'); }
    setPage(0);
  };

  const SortHeader = ({ field, children, sx: sxProp }) => (
    <TableCell onClick={() => handleSort(field)} sx={{ cursor: 'pointer', fontWeight: 700, userSelect: 'none', whiteSpace: 'nowrap', ...sxProp }}>
      {children}
      {sortField === field && (sortDir === 'asc' ? <ArrowUpIcon sx={{ fontSize: 14, ml: 0.3, verticalAlign: 'middle' }} /> : <ArrowDownIcon sx={{ fontSize: 14, ml: 0.3, verticalAlign: 'middle' }} />)}
    </TableCell>
  );

  // ── Actions ──
  const handleStatusChange = useCallback(async (prospect, newStatus) => {
    await updateContact.mutateAsync({ id: prospect.id, data: { ...prospect.data, outreach_status: newStatus } });
  }, [updateContact]);

  const handleSegmentChange = useCallback(async (prospect, newSegment) => {
    await updateContact.mutateAsync({ id: prospect.id, data: { ...prospect.data, segment: newSegment } });
  }, [updateContact]);

  const handleLogMission = useCallback(async (prospect, step, isFollowUp = false, channelOverride = null) => {
    try {
      const effectiveChannel = channelOverride || step.channel;
      const baseXP = getXP(step.actionType);
      const mult = stats.streak.multiplier || 1;
      const earnedXP = Math.round(baseXP * mult);
      await logAction.mutateAsync({
        contactId: prospect.id, userId: user?.id, actionType: step.actionType,
        channel: effectiveChannel, cadenceDay: step.day, notes: `Completed: ${step.label}`,
      });
      const currentStatus = prospect.data?.outreach_status || prospect.data?.outreachStatus || 'new';
      const nextStatus = getNextStatus(currentStatus, step.actionType);
      const newData = { ...prospect.data };
      if (nextStatus) newData.outreach_status = nextStatus;
      // Clear follow-up date when follow-up mission is completed
      if (isFollowUp || step.day === 0) {
        delete newData.follow_up_date;
        delete newData.follow_up_note;
      }
      if (nextStatus || isFollowUp || step.day === 0) {
        await updateContact.mutateAsync({ id: prospect.id, data: newData });
      }
      setLastCompletedProspectId(prospect.id);
      setSnack({ msg: `+${earnedXP} XP${mult > 1 ? ` (×${mult})` : ''} — ${step.label}`, severity: 'success', prospectId: prospect.id });
    } catch {
      setSnack({ msg: 'Failed to log action', severity: 'error' });
    }
  }, [logAction, updateContact, user?.id, stats.streak.multiplier]);

  const handleConfirmAction = useCallback(async (actionData) => {
    try {
      await logAction.mutateAsync({
        contactId: actionData.contactId, userId: user?.id, actionType: actionData.actionType,
        channel: actionData.channel, cadenceDay: actionData.cadenceDay,
        templateUsed: actionData.templateUsed || null, notes: actionData.notes || '',
        messageSubject: actionData.messageSubject || null,
        messageBody: actionData.messageBody || null,
        nature: actionData.nature || null,
        customPrompt: actionData.customPrompt || null,
        aiUsed: actionData.aiUsed || false,
      });
      const prospect = prospects.find(p => p.id === actionData.contactId);
      if (prospect) {
        const currentStatus = prospect.data?.outreach_status || prospect.data?.outreachStatus || 'new';
        const nextStatus = getNextStatus(currentStatus, actionData.actionType);
        if (nextStatus) {
          await updateContact.mutateAsync({ id: prospect.id, data: { ...prospect.data, outreach_status: nextStatus } });
        }
      }
      setActionDialog(null);
      const baseXP = getXP(actionData.actionType);
      const mult = stats.streak.multiplier || 1;
      const earnedXP = Math.round(baseXP * mult);
      setSnack({ msg: `+${earnedXP} XP${mult > 1 ? ` (×${mult})` : ''}`, severity: 'success' });
    } catch {
      setSnack({ msg: 'Failed to log action', severity: 'error' });
    }
  }, [logAction, updateContact, user?.id, prospects, stats.streak.multiplier]);

  const handleLogResponse = useCallback(async ({ contactId, notes, followUpDate, sentiment, channel, messageBody }) => {
    try {
      await logAction.mutateAsync({
        contactId, userId: user?.id, actionType: 'response',
        channel: channel || 'email', notes: notes || 'Prospect replied',
        messageBody: messageBody || null,
        responseSentiment: sentiment || 'neutral',
      });
      const prospect = prospects.find(p => p.id === contactId);
      if (prospect) {
        let newStatus;
        if (sentiment === 'declined') newStatus = 'declined';
        else if (sentiment === 'positive') newStatus = 'responded';
        else newStatus = 'follow_up';
        const newData = { ...prospect.data, outreach_status: newStatus };
        if (followUpDate) newData.follow_up_date = followUpDate;
        if (sentiment === 'positive') newData.follow_up_note = 'Hot lead — follow up to book!';
        await updateContact.mutateAsync({ id: prospect.id, data: newData });
      }
      setResponseDialog(null);
      setSnack({ msg: `+25 XP — ${sentiment === 'positive' ? 'Hot lead!' : sentiment === 'declined' ? 'Declined — cadence stopped' : 'Response noted'}`, severity: sentiment === 'declined' ? 'warning' : 'success' });
    } catch {
      setSnack({ msg: 'Failed to log response', severity: 'error' });
    }
  }, [logAction, updateContact, user?.id, prospects]);

  const handleBookConvert = useCallback(async (prospect, actionType) => {
    try {
      const baseXP = getXP(actionType);
      const mult = stats.streak.multiplier || 1;
      const earnedXP = Math.round(baseXP * mult);
      await logAction.mutateAsync({
        contactId: prospect.id, userId: user?.id, actionType,
        channel: 'in_person', notes: actionType === 'booked' ? 'Meeting booked!' : 'Converted to client!',
      });
      const newStatus = actionType === 'booked' ? 'booked' : 'client';
      const newData = { ...prospect.data, outreach_status: newStatus };
      delete newData.follow_up_date;
      delete newData.follow_up_note;
      await updateContact.mutateAsync({ id: prospect.id, data: newData });
      setSnack({ msg: `+${earnedXP} XP${mult > 1 ? ` (×${mult})` : ''} — ${actionType === 'booked' ? 'Meeting booked!' : 'Client converted!'}`, severity: 'success' });
    } catch {
      setSnack({ msg: 'Failed to log action', severity: 'error' });
    }
  }, [logAction, updateContact, user?.id, stats.streak.multiplier]);

  const handleDismissMission = useCallback(async (prospect, action, snoozeDate) => {
    try {
      const newData = { ...prospect.data };
      if (action === 'snooze') {
        newData.snoozed_until = snoozeDate;
        await updateContact.mutateAsync({ id: prospect.id, data: newData });
        setSnack({ msg: `${prospect.name} snoozed until ${snoozeDate}`, severity: 'info' });
      } else if (action === 'deactivate') {
        newData.outreach_status = 'new';
        delete newData.follow_up_date;
        delete newData.follow_up_note;
        delete newData.snoozed_until;
        await updateContact.mutateAsync({ id: prospect.id, data: newData });
        setSnack({ msg: `${prospect.name} deactivated`, severity: 'info' });
      } else if (action === 'decline') {
        newData.outreach_status = 'declined';
        delete newData.follow_up_date;
        delete newData.follow_up_note;
        delete newData.snoozed_until;
        await updateContact.mutateAsync({ id: prospect.id, data: newData });
        setSnack({ msg: `${prospect.name} marked declined`, severity: 'warning' });
      }
      setDismissDialog(null);
    } catch {
      setSnack({ msg: 'Failed to update prospect', severity: 'error' });
    }
  }, [updateContact]);

  const handleStartCadence = useCallback(async (prospect) => {
    const status = prospect.data?.outreach_status || prospect.data?.outreachStatus || 'new';
    // Auto-activate new prospects so they appear in missions
    if (status === 'new') {
      try {
        await updateContact.mutateAsync({ id: prospect.id, data: { ...prospect.data, outreach_status: 'active' } });
      } catch { /* proceed even if activation fails */ }
    }
    const seg = prospect.data?.segment || 'unknown';
    const prospectActions = actionsByContact.get(prospect.id) || [];
    const cadence = getCadenceState(prospectActions, seg);
    const step = cadence.nextStep || getCadenceForSegment(seg)[0];
    setActionDialog({ prospect, step });
  }, [actionsByContact, updateContact]);

  // Batch activation with auto-research
  const handleBatchActivate = useCallback(async () => {
    if (selectedForBatch.size === 0) return;
    setActivating(true);
    const selected = prospects.filter(p => selectedForBatch.has(p.id));
    let ok = 0;
    for (const p of selected) {
      setActivateProgress(`Researching ${ok + 1} of ${selected.length}: ${p.name}...`);
      // Auto-research (saves ai_research to DB)
      try {
        await api.post('/ai/research-prospect', { contactId: p.id });
      } catch (err) {
        console.warn(`Research failed for ${p.name}:`, err.message);
      }
      // Fetch fresh contact data AFTER research so we don't overwrite ai_research
      let freshData = p.data;
      try {
        const freshContact = await api.get(`/contacts/${p.id}`);
        freshData = freshContact.data || p.data;
      } catch { /* fall back to stale data */ }
      // Activate
      try {
        await updateContact.mutateAsync({ id: p.id, data: { ...freshData, outreach_status: 'active' } });
        ok++;
      } catch (err) {
        console.warn(`Activate failed for ${p.name}:`, err.message);
      }
    }
    queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all });
    setSelectedForBatch(new Set());
    setActivating(false);
    setActivateProgress('');
    setSnack({ msg: `${ok} prospects researched & activated — Day 1 missions ready!`, severity: 'success' });
  }, [selectedForBatch, prospects, updateContact, queryClient]);

  const handleToggleBatch = useCallback((id) => {
    setSelectedForBatch(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else if (next.size < 25) next.add(id);
      return next;
    });
  }, []);

  // ── Render ──
  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* ── Header + Stats Bar ── */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Outreach</Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          {stats.streak.streak > 0 && (
            <Chip icon={<FireIcon sx={{ color: '#F97316 !important' }} />}
              label={`${stats.streak.streak} day${stats.streak.streak > 1 ? 's' : ''} · ×${stats.streak.multiplier}`}
              size="small" sx={{ bgcolor: 'rgba(249,115,22,0.1)', color: '#F97316', fontWeight: 600 }} />
          )}
          <Chip icon={<StarIcon sx={{ color: '#FBBF24 !important' }} />}
            label={`${stats.totalXP} XP`}
            size="small" sx={{ bgcolor: 'rgba(251,191,36,0.1)', color: '#FBBF24', fontWeight: 700 }} />
          <Chip icon={<TrophyIcon sx={{ color: '#A78BFA !important' }} />}
            label={`Lv${stats.level.level} ${stats.level.title}`}
            size="small" sx={{ bgcolor: 'rgba(167,139,250,0.1)', color: '#A78BFA', fontWeight: 600 }} />
          {stats.level.nextLevelXP && (
            <Box sx={{ width: 80 }}>
              <LinearProgress variant="determinate" value={stats.level.progress * 100}
                sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(251,191,36,0.1)', '& .MuiLinearProgress-bar': { bgcolor: '#FBBF24' } }} />
            </Box>
          )}
          <Tooltip title="View Templates">
            <IconButton size="small" onClick={() => setTemplatesOpen(true)}><TemplateIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Outreach Settings">
            <IconButton size="small" onClick={() => setOutreachSettingsOpen(true)}><SettingsIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {/* ── Missions Section ── */}
      <Paper sx={{ mb: 2, overflow: 'hidden' }}>
        <Box onClick={() => setMissionsExpanded(!missionsExpanded)}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FlagIcon sx={{ color: missions.length > 0 ? '#EF4444' : '#94A3B8' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Missions ({missions.length})
            </Typography>
            {missions.filter(m => m.urgency === 'overdue').length > 0 && (
              <Chip label={`${missions.filter(m => m.urgency === 'overdue').length} overdue`} size="small"
                sx={{ bgcolor: '#EF4444', color: '#fff', fontSize: '0.6rem', fontWeight: 700 }} />
            )}
          </Box>
          {missionsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        <Collapse in={missionsExpanded}>
          <Divider />
          {/* Focus channel selector */}
          {missions.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 1, bgcolor: 'action.hover', flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, mr: 0.5, color: 'text.secondary' }}>Focus:</Typography>
              {[{ key: null, label: 'All', icon: null },
                { key: 'phone', label: 'Calls', icon: PhoneIcon },
                { key: 'email', label: 'Email', icon: EmailIcon },
                { key: 'text', label: 'Text', icon: TextIcon },
                { key: 'instagram_dm', label: 'DM', icon: InstagramIcon },
                { key: 'social', label: 'Social', icon: InstagramIcon },
              ].map(ch => {
                const Icon = ch.icon;
                return (
                  <Chip
                    key={ch.key || 'all'}
                    icon={Icon ? <Icon sx={{ fontSize: 14 }} /> : undefined}
                    label={ch.label}
                    size="small"
                    onClick={() => setFocusChannel(ch.key)}
                    sx={{
                      height: 22, fontSize: '0.6rem', fontWeight: focusChannel === ch.key ? 700 : 400,
                      bgcolor: focusChannel === ch.key ? 'primary.main' : 'transparent',
                      color: focusChannel === ch.key ? 'white' : 'text.secondary',
                      '& .MuiChip-icon': { color: focusChannel === ch.key ? 'white' : 'text.secondary' },
                    }}
                  />
                );
              })}
            </Box>
          )}
          {missions.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No active missions. Select prospects below and activate them.</Typography>
            </Box>
          ) : (
            <Stack spacing={0} sx={{ maxHeight: 500, overflow: 'auto' }}>
              {missions.slice(0, 20).map((m, i) => {
                const research = m.prospect.data?.ai_research;
                const bestAngle = research?.outreach?.best_angle;
                const gap = research?.media?.visible_gap;
                const trigger = research?.outreach?.trigger;
                const urgencyColors = { overdue: '#EF4444', today: '#F59E0B', new: '#3B82F6', follow_up: '#A855F7' };
                const urgencyLabels = { overdue: 'OVERDUE', today: 'DUE TODAY', new: 'NEW', follow_up: 'FOLLOW UP' };
                const effectiveChannel = focusChannel || m.step.channel;
                const Icon = CHANNEL_ICONS[effectiveChannel] || EmailIcon;

                return (
                  <Box key={i} sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'flex-start', gap: 1.5, p: 1.5,
                    borderBottom: '1px solid', borderColor: 'divider',
                    borderLeft: `3px solid ${urgencyColors[m.urgency] || '#94A3B8'}`,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => setSelectedProspectId(m.prospect.id)}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
                      <AgentAvatar name={m.prospect.name} size={isMobile ? 28 : 32} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{m.prospect.name}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>{m.prospect.company}</Typography>
                          <SegmentChip segment={m.prospect.data?.segment || 'unknown'} />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          <Icon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                          Day {m.step.day}: {m.step.label}{focusChannel && focusChannel !== m.step.channel ? ` → ${CHANNEL_LABELS[focusChannel]}` : ''}
                        </Typography>
                        {(bestAngle || gap) && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.3, color: '#A855F7', fontStyle: 'italic' }}>
                            {bestAngle || gap}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, ...(isMobile && { pl: 5, pt: 0.5 }) }} onClick={e => e.stopPropagation()}>
                      <Chip label={urgencyLabels[m.urgency] || m.urgency} size="small"
                        sx={{ bgcolor: urgencyColors[m.urgency] || '#94A3B8', color: '#fff', fontSize: '0.55rem', fontWeight: 700, height: 20 }} />
                      <Chip label={`+${getXP(m.step.actionType)}`} size="small"
                        sx={{ bgcolor: 'rgba(251,191,36,0.1)', color: '#FBBF24', fontWeight: 700, fontSize: '0.6rem', height: 20 }} />
                      <Button variant="contained" size="small" onClick={() => handleLogMission(m.prospect, m.step, m.isFollowUp, focusChannel)}
                        sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 50, py: 0.3, minHeight: { xs: 40, md: 'auto' }, px: 1.5 }}>
                        Done
                      </Button>
                      <Tooltip title="Remove from missions">
                        <IconButton size="small" onClick={() => setDismissDialog(m.prospect)}
                          sx={{ color: 'text.secondary', '&:hover': { color: '#EF4444' } }}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                );
              })}
              {missions.length > 20 && (
                <Typography variant="caption" color="text.secondary" sx={{ p: 1, textAlign: 'center', display: 'block' }}>
                  + {missions.length - 20} more missions
                </Typography>
              )}
            </Stack>
          )}
        </Collapse>
      </Paper>

      {/* ── Activity Log ── */}
      {totalLogActions > 0 && (
        <Paper sx={{ mb: 2, overflow: 'hidden' }}>
          <Box onClick={() => setActivityLogExpanded(!activityLogExpanded)}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon sx={{ color: '#22C55E' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Activity Log
              </Typography>
              <Chip label={`${totalLogActions} actions`} size="small"
                sx={{ fontSize: '0.6rem', fontWeight: 600, height: 20 }} />
            </Box>
            {activityLogExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
          <Collapse in={activityLogExpanded}>
            <Divider />
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {activityLog.map((day) => {
                const isExpanded = day.isToday || expandedLogDates.has(day.date);
                return (
                  <Box key={day.date}>
                    <Box
                      onClick={() => {
                        if (day.isToday) return;
                        setExpandedLogDates(prev => {
                          const next = new Set(prev);
                          next.has(day.date) ? next.delete(day.date) : next.add(day.date);
                          return next;
                        });
                      }}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 1.5, py: 0.75, bgcolor: day.isToday ? 'rgba(34,197,94,0.06)' : 'transparent',
                        cursor: day.isToday ? 'default' : 'pointer',
                        '&:hover': { bgcolor: day.isToday ? 'rgba(34,197,94,0.06)' : 'action.hover' },
                        borderBottom: '1px solid', borderColor: 'divider',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 600, color: day.isToday ? '#22C55E' : 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {day.isToday ? 'Today' : new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        {' — '}{day.prospects.length} prospect{day.prospects.length !== 1 ? 's' : ''}, {day.totalActions} action{day.totalActions !== 1 ? 's' : ''}
                      </Typography>
                      {!day.isToday && (
                        isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />
                      )}
                    </Box>
                    <Collapse in={isExpanded}>
                      {day.prospects.map(({ prospect: p, actions: pActions, channels }) => {
                        const dayUserIds = [...new Set(pActions.map(a => a.userId).filter(Boolean))];
                        return (
                        <Box key={p.id} sx={{
                          display: 'flex', alignItems: isMobile ? 'stretch' : 'center',
                          flexDirection: isMobile ? 'column' : 'row',
                          gap: 1, px: 1.5, py: 1,
                          borderBottom: '1px solid', borderColor: 'divider',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0, cursor: 'pointer' }}
                            onClick={() => setSelectedProspectId(p.id)}>
                            <AgentAvatar name={p.name} size={24} />
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }} noWrap>{p.name}</Typography>
                            {channels.map(ch => {
                              const ChIcon = CHANNEL_ICONS[ch];
                              return ChIcon ? <ChIcon key={ch} sx={{ fontSize: 14, color: 'text.disabled' }} /> : null;
                            })}
                            <Typography variant="caption" color="text.disabled">({pActions.length})</Typography>
                            {dayUserIds.map(uid => {
                              const u = userMap.get(uid);
                              if (!u) return null;
                              return (
                                <Tooltip key={uid} title={u.fullName} arrow>
                                  <Box sx={{
                                    width: 18, height: 18, borderRadius: '50%',
                                    bgcolor: u.accentColor || '#64748B', color: '#fff',
                                    fontSize: '0.5rem', fontWeight: 700,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}>
                                    {u.fullName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                                  </Box>
                                </Tooltip>
                              );
                            })}
                          </Box>
                          <Button size="small" variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              const seg = p.data?.segment || 'unknown';
                              const cActions = actionsByContact.get(p.id) || [];
                              const cadence = getCadenceState(cActions, seg);
                              const step = cadence.nextStep || getCadenceForSegment(seg)[0];
                              setActionDialog({ prospect: p, step });
                            }}
                            sx={{ textTransform: 'none', fontSize: '0.65rem', py: 0.2, minWidth: 0, flexShrink: 0 }}
                          >
                            + Log More
                          </Button>
                        </Box>
                      );
                      })}
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
          </Collapse>
        </Paper>
      )}

      {/* ── Prospects Table ── */}
      <Paper sx={{ p: 1.5, mb: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup size="small" value={listMode} exclusive
          onChange={(_, v) => { if (v) { setListMode(v); setPage(0); } }}>
          <ToggleButton value="prospects" sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.5 }}>Prospects</ToggleButton>
          <ToggleButton value="all" sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.5 }}>All</ToggleButton>
        </ToggleButtonGroup>
        <TextField size="small" placeholder="Search..." value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: isMobile ? 120 : 200, flex: isMobile ? 1 : 'none' }} />
        <FormControl size="small" sx={{ minWidth: isMobile ? 100 : 120, flex: isMobile ? 1 : 'none' }}>
          <InputLabel>Segment</InputLabel>
          <Select value={segmentFilter} label="Segment" onChange={e => { setSegmentFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">All ({prospects.length})</MenuItem>
            {SEGMENTS.map(s => <MenuItem key={s} value={s}>{SEGMENT_LABELS[s]} ({segmentCounts[s] || 0})</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: isMobile ? 100 : 120, flex: isMobile ? 1 : 'none' }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={e => { setStatusFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">All ({prospects.length})</MenuItem>
            {OUTREACH_STATUSES.map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace(/_/g, ' ')} ({statusCounts[s] || 0})</MenuItem>)}
          </Select>
        </FormControl>
        {!isMobile && <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Office</InputLabel>
          <Select value={officeFilter} label="Office" onChange={e => { setOfficeFilter(e.target.value); setTeamFilter('all'); setPage(0); }}>
            <MenuItem value="all">All ({offices.length} offices)</MenuItem>
            {offices.slice(0, 50).map(o => <MenuItem key={o} value={o}>{o} ({officeCounts[o] || 0})</MenuItem>)}
          </Select>
        </FormControl>}
        {!isMobile && teams.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Team</InputLabel>
            <Select value={teamFilter} label="Team" onChange={e => { setTeamFilter(e.target.value); setOfficeFilter('all'); setPage(0); }}>
              <MenuItem value="all">All ({teams.length} teams)</MenuItem>
              {teams.map(t => <MenuItem key={t} value={t}>{t} ({teamCounts[t] || 0})</MenuItem>)}
              <MenuItem value="_none">No Team ({teamCounts._none || 0})</MenuItem>
            </Select>
          </FormControl>
        )}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedForBatch.size > 0 && (
            <Button variant="contained" size="small" startIcon={activating ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <AddIcon />}
              onClick={handleBatchActivate} disabled={activating}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
              {activating ? activateProgress || 'Activating...' : `Activate ${selectedForBatch.size} Selected`}
            </Button>
          )}
          <Typography variant="caption" color="text.secondary">{filtered.length} prospects</Typography>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {!isMobile && <TableCell sx={{ width: 40, px: 1 }}>
                <Tooltip title="Select new prospects to batch-activate"><Checkbox size="small" disabled /></Tooltip>
              </TableCell>}
              <SortHeader field="name" sx={isMobile ? { pr: 0.5 } : undefined}>Name</SortHeader>
              {!isMobile && <SortHeader field="office">Office</SortHeader>}
              {isMobile
                ? <SortHeader field="experience" sx={{ px: 0, textAlign: 'center' }}>Yrs</SortHeader>
                : <SortHeader field="experience">Experience</SortHeader>}
              {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Segment</TableCell>}
              <SortHeader field="status" sx={isMobile ? { pl: 1 } : undefined}>Status</SortHeader>
              {!isMobile && <SortHeader field="actions">Cadence</SortHeader>}
              <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(p => {
              const status = p.data?.outreach_status || p.data?.outreachStatus || 'new';
              const segment = p.data?.segment || 'unknown';
              const actions = actionsByContact.get(p.id) || [];
              const cadence = getCadenceState(actions, segment);
              const isNew = status === 'new';
              const exp = experienceById.get(p.id);

              return (
                <TableRow key={p.id} hover sx={{ cursor: 'pointer' }}
                  onClick={() => setSelectedProspectId(p.id)}>
                  {!isMobile && <TableCell sx={{ px: 1 }} onClick={e => e.stopPropagation()}>
                    {isNew && (
                      <Checkbox size="small" checked={selectedForBatch.has(p.id)}
                        onChange={() => handleToggleBatch(p.id)} />
                    )}
                  </TableCell>}
                  <TableCell sx={{ fontWeight: 600, ...(isMobile && { pr: 0.5 }) }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AgentAvatar name={p.name} size={28} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name}</Typography>
                        {p.data?.team && <Typography variant="caption" color="text.secondary">{p.data.team}</Typography>}
                      </Box>
                      {!isMobile && prospectOwners.has(p.id) && (
                        <Box sx={{ display: 'flex', gap: 0.25, ml: 'auto' }}>
                          {[...prospectOwners.get(p.id)].map(uid => {
                            const u = userMap.get(uid);
                            if (!u) return null;
                            return (
                              <Tooltip key={uid} title={u.fullName} arrow>
                                <Box sx={{
                                  width: 20, height: 20, borderRadius: '50%',
                                  bgcolor: u.accentColor || '#64748B', color: '#fff',
                                  fontSize: '0.55rem', fontWeight: 700,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                  {u.fullName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  {!isMobile && <TableCell sx={{ fontSize: '0.8rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.data?.dre_broker_moved ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.company}</Box>
                        <Tooltip title={`DRE: now with ${p.data.dre_responsible_broker || 'another broker'}`}>
                          <Chip label="moved?" size="small" variant="outlined"
                            sx={{ flexShrink: 0, height: 16, fontSize: '0.6rem', color: 'text.secondary', borderColor: 'divider', '& .MuiChip-label': { px: 0.6 } }} />
                        </Tooltip>
                      </Box>
                    ) : p.company}
                  </TableCell>}
                  {isMobile ? (
                    <TableCell sx={{ px: 0, textAlign: 'center', fontSize: '0.8rem', fontWeight: 600, color: exp ? 'text.primary' : 'text.disabled', whiteSpace: 'nowrap' }}>
                      {formatExperience(exp, true)}
                    </TableCell>
                  ) : (
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {exp ? (
                        <Tooltip title={experienceTooltip(exp)}>
                          <Typography variant="body2" component="span" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{formatExperience(exp)}</Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                  )}
                  {!isMobile && <TableCell><SegmentChip segment={segment} /></TableCell>}
                  <TableCell sx={isMobile ? { pl: 1 } : undefined}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <StatusChip status={status} />
                      {p.type === 'client' && (
                        <Chip label="Client" size="small" sx={{ bgcolor: '#22C55E', color: '#fff', fontWeight: 600, fontSize: '0.6rem', height: 20 }} />
                      )}
                    </Box>
                  </TableCell>
                  {!isMobile && <TableCell>
                    {cadence.currentStep > 0 ? (
                      <Tooltip title={cadence.nextStep ? `Next: Day ${cadence.nextStep.day} — ${cadence.nextStep.label}` : 'Complete'}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {cadence.currentStep}/{cadence.cadenceSteps.length}
                          </Typography>
                          <LinearProgress variant="determinate"
                            value={(cadence.currentStep / cadence.cadenceSteps.length) * 100}
                            sx={{ width: 50, height: 4, borderRadius: 2 }} />
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>}
                  <TableCell sx={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Log Action">
                        <IconButton size="small" color="primary"
                          onClick={() => handleStartCadence(p)}>
                          <PlayIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Log Response">
                        <IconButton size="small" sx={{ color: '#4ADE80' }}
                          onClick={() => setResponseDialog(p)}>
                          <ReplyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          sx={{ '& .MuiTablePagination-toolbar': { flexWrap: { xs: 'wrap', md: 'nowrap' } }, '& .MuiTablePagination-selectLabel': { display: { xs: 'none', md: 'block' } } }}
        />
      </TableContainer>

      {/* ── Dialogs ── */}
      {selectedProspect && (
        <ProspectDetailDialog
          prospect={selectedProspect}
          actions={actionsByContact.get(selectedProspect.id) || []}
          onClose={() => setSelectedProspectId(null)}
          onStatusChange={handleStatusChange}
          onSegmentChange={handleSegmentChange}
          onLogAction={(step) => setActionDialog({ prospect: selectedProspect, step })}
          onLogResponse={() => setResponseDialog(selectedProspect)}
          onBookConvert={handleBookConvert}
          settings={settings?.data}
        />
      )}
      {actionDialog && (
        <LogActionDialog prospect={actionDialog.prospect} step={actionDialog.step}
          settings={settings?.data} onConfirm={handleConfirmAction}
          onCancel={() => setActionDialog(null)} updateContact={updateContact} />
      )}
      {responseDialog && (
        <LogResponseDialog prospect={responseDialog}
          onConfirm={handleLogResponse} onCancel={() => setResponseDialog(null)} />
      )}
      {dismissDialog && (
        <DismissMissionDialog prospect={dismissDialog}
          onConfirm={handleDismissMission} onCancel={() => setDismissDialog(null)} />
      )}
      {templatesOpen && (
        <Dialog open onClose={() => setTemplatesOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Templates
            <Button onClick={() => setTemplatesOpen(false)}>Close</Button>
          </DialogTitle>
          <DialogContent><TemplatesTab settings={settings?.data} /></DialogContent>
        </Dialog>
      )}
      {outreachSettingsOpen && (
        <OutreachSettingsDialog
          settings={settings}
          onClose={() => setOutreachSettingsOpen(false)}
        />
      )}

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={isMobile ? { bottom: 'calc(56px + env(safe-area-inset-bottom) + 8px)' } : undefined}>
        <Alert severity={snack?.severity || 'success'} variant="filled" onClose={() => setSnack(null)}
          action={snack?.prospectId ? (
            <Button color="inherit" size="small" sx={{ fontWeight: 700, fontSize: '0.7rem' }}
              onClick={() => { setSelectedProspectId(snack.prospectId); setSnack(null); }}>
              Log More
            </Button>
          ) : undefined}>
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ── Dismiss Mission Dialog ───────────────────────────────────
function DismissMissionDialog({ prospect, onConfirm, onCancel }) {
  const [snoozeDate, setSnoozeDate] = useState('');
  const quickSnooze = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  return (
    <Dialog open onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Remove from Missions
        <Typography variant="body2" color="text.secondary">{prospect.name} — {prospect.company}</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Snooze */}
          <Paper variant="outlined" sx={{ p: 2, cursor: 'pointer', '&:hover': { borderColor: '#3B82F6' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SnoozeIcon sx={{ color: '#3B82F6' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Snooze</Typography>
              <Typography variant="caption" color="text.secondary">— come back later</Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              {[{ label: '3 days', days: 3 }, { label: '1 week', days: 7 }, { label: '2 weeks', days: 14 }, { label: '1 month', days: 30 }].map(opt => (
                <Chip key={opt.days} label={opt.label} size="small" clickable
                  onClick={() => onConfirm(prospect, 'snooze', quickSnooze(opt.days))}
                  sx={{ fontSize: '0.7rem' }} />
              ))}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField label="Custom date" type="date" size="small" value={snoozeDate}
                onChange={e => setSnoozeDate(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
              <Button size="small" variant="outlined" disabled={!snoozeDate}
                onClick={() => onConfirm(prospect, 'snooze', snoozeDate)}
                sx={{ textTransform: 'none' }}>
                Snooze
              </Button>
            </Stack>
          </Paper>

          {/* Deactivate */}
          <Paper variant="outlined" sx={{ p: 2, cursor: 'pointer', '&:hover': { borderColor: '#F59E0B' } }}
            onClick={() => onConfirm(prospect, 'deactivate')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeactivateIcon sx={{ color: '#F59E0B' }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Deactivate</Typography>
                <Typography variant="caption" color="text.secondary">
                  Resets to "new" — keeps contact & history, removes from pipeline
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Decline */}
          <Paper variant="outlined" sx={{ p: 2, cursor: 'pointer', '&:hover': { borderColor: '#EF4444' } }}
            onClick={() => onConfirm(prospect, 'decline')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BlockIcon sx={{ color: '#EF4444' }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#EF4444' }}>Mark Declined</Typography>
                <Typography variant="caption" color="text.secondary">
                  Permanently removes from outreach — not interested
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Log Response Dialog ──────────────────────────────────────
const SENTIMENT_OPTIONS = [
  { value: 'positive', label: 'Positive', desc: 'Interested, wants to talk/book', color: '#4ADE80', icon: '🔥' },
  { value: 'neutral', label: 'Neutral', desc: 'Acknowledged, not committed', color: '#FBBF24', icon: '💬' },
  { value: 'declined', label: 'Declined', desc: 'Not interested, stops cadence', color: '#F87171', icon: '✋' },
];

function LogResponseDialog({ prospect, onConfirm, onCancel }) {
  const [notes, setNotes] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [sentiment, setSentiment] = useState('neutral');
  const [channel, setChannel] = useState('email');
  const [followUpDate, setFollowUpDate] = useState('');
  const { isMobile } = useMobile();

  return (
    <Dialog open onClose={onCancel} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Log Client Reply
        <Typography variant="body2" color="text.secondary">{prospect.name} — {prospect.company}</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Sentiment selector */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: -1 }}>How did they respond?</Typography>
          <Stack direction="row" spacing={1}>
            {SENTIMENT_OPTIONS.map(opt => (
              <Paper key={opt.value} variant="outlined"
                onClick={() => setSentiment(opt.value)}
                sx={{
                  flex: 1, p: 1.5, cursor: 'pointer', textAlign: 'center',
                  borderColor: sentiment === opt.value ? opt.color : 'divider',
                  borderWidth: sentiment === opt.value ? 2 : 1,
                  bgcolor: sentiment === opt.value ? `${opt.color}12` : 'transparent',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: opt.color },
                }}>
                <Typography sx={{ fontSize: '1.4rem', mb: 0.5 }}>{opt.icon}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{opt.label}</Typography>
                <Typography variant="caption" color="text.secondary">{opt.desc}</Typography>
              </Paper>
            ))}
          </Stack>

          {/* Channel */}
          <FormControl size="small" fullWidth>
            <InputLabel>Reply channel</InputLabel>
            <Select value={channel} label="Reply channel" onChange={e => setChannel(e.target.value)}>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="text">Text</MenuItem>
              <MenuItem value="phone">Phone</MenuItem>
              <MenuItem value="instagram_dm">Instagram DM</MenuItem>
              <MenuItem value="in_person">In Person</MenuItem>
            </Select>
          </FormControl>

          {/* Their reply */}
          <TextField label="Paste their reply (optional)" multiline rows={3} value={messageBody}
            onChange={e => setMessageBody(e.target.value)} size="small" fullWidth
            placeholder="Copy-paste what they said so you have a record..." />

          {/* Notes */}
          <TextField label="Your notes" multiline rows={2} value={notes}
            onChange={e => setNotes(e.target.value)} size="small" fullWidth
            placeholder="Any context or next steps..." />

          {/* Follow-up date — only for positive/neutral */}
          {sentiment !== 'declined' && (
            <TextField label="Follow-up date" type="date" value={followUpDate}
              onChange={e => setFollowUpDate(e.target.value)} size="small" fullWidth
              InputLabelProps={{ shrink: true }}
              helperText={sentiment === 'positive' ? 'When to follow up to book?' : 'When to check in again?'} />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" startIcon={<ReplyIcon />}
          onClick={() => onConfirm({ contactId: prospect.id, notes, followUpDate: followUpDate || null, sentiment, channel, messageBody: messageBody || null })}
          sx={{ bgcolor: SENTIMENT_OPTIONS.find(s => s.value === sentiment)?.color || '#4ADE80',
            '&:hover': { filter: 'brightness(0.9)' } }}>
          Log Reply (+25 XP)
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ── Prospect Detail Dialog ──────────────────────────────────
function ProspectDetailDialog({ prospect, actions, onClose, onStatusChange, onSegmentChange, onLogAction, onLogResponse, onBookConvert, settings }) {
  const cadence = useMemo(() => getCadenceState(actions, prospect.data?.segment || 'unknown'), [actions, prospect.data?.segment]);
  const [expandedMsg, setExpandedMsg] = useState(null);
  const [expandedLogMsg, setExpandedLogMsg] = useState(null);
  const { isMobile } = useMobile();
  const status = prospect.data?.outreach_status || prospect.data?.outreachStatus || 'new';
  const segment = prospect.data?.segment || 'unknown';
  const experience = useMemo(() => getExperience(prospect.data), [prospect.data]);
  const website = prospect.data?.website || '';
  const updateContact = useUpdateContact();
  const queryClient = useQueryClient();

  // AI Research state — sync from prop when prospect changes
  const savedResearch = prospect.data?.ai_research || prospect.data?.aiResearch || null;
  const [research, setResearch] = useState(savedResearch);
  const [researchedAt, setResearchedAt] = useState(prospect.data?.ai_research_at || prospect.data?.aiResearchAt || null);
  const [researchLoading, setResearchLoading] = useState(false);
  const [researchError, setResearchError] = useState(null);
  const [showResearch, setShowResearch] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState({}); // field -> value accepted from AI
  const [revertValues, setRevertValues] = useState({}); // field -> original value before AI update
  const researchRef = useRef(null);

  const handleSaveScreenshot = useCallback(async () => {
    if (!researchRef.current) return;
    try {
      const canvas = await html2canvas(researchRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
        onclone: (doc) => {
          // Fix MUI Chip rendering — force explicit sizes
          doc.querySelectorAll('.MuiChip-root').forEach(chip => {
            chip.style.display = 'inline-flex';
            chip.style.height = 'auto';
            chip.style.minHeight = '20px';
            chip.style.fontSize = '11px';
            chip.style.lineHeight = '1.2';
            chip.style.padding = '2px 8px';
          });
          doc.querySelectorAll('.MuiChip-label').forEach(label => {
            label.style.padding = '0';
            label.style.fontSize = '11px';
          });
          doc.querySelectorAll('.MuiLinearProgress-root').forEach(bar => {
            bar.style.overflow = 'hidden';
          });
        },
      });
      const link = document.createElement('a');
      const safeName = (prospect.name || 'prospect').replace(/[^a-zA-Z0-9]/g, '_');
      link.download = `${safeName}_research.jpg`;
      link.href = canvas.toDataURL('image/jpeg', 0.92);
      link.click();
    } catch (err) {
      console.error('Screenshot failed:', err);
    }
  }, [prospect.name]);

  // Re-sync research from prop when the contact is refetched (e.g. after research saved on backend)
  useEffect(() => {
    const fresh = prospect.data?.ai_research || prospect.data?.aiResearch || null;
    if (fresh && fresh !== research) {
      setResearch(fresh);
      setResearchedAt(prospect.data?.ai_research_at || prospect.data?.aiResearchAt || null);
    }
  }, [prospect.data?.ai_research, prospect.data?.aiResearch]);

  // Determine if research is structured JSON or legacy text
  const isStructured = research && typeof research === 'object' && research.contact_updates;

  const handleResearch = useCallback(async (forceRefresh = false) => {
    if (research && !forceRefresh) {
      setShowResearch(true);
      return;
    }
    setResearchLoading(true);
    setResearchError(null);
    setShowResearch(true);
    setPendingUpdates({});
    setRevertValues({});
    try {
      const result = await api.post('/ai/research-prospect', { contactId: prospect.id });
      const r = result.structured ? result.research : result.research;
      setResearch(r);
      setResearchedAt(result.researchedAt);
      // Invalidate contacts cache so the live prospect gets the saved research
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all });
    } catch (err) {
      setResearchError(err.message || 'Research failed');
    } finally {
      setResearchLoading(false);
    }
  }, [research, prospect.id, queryClient]);

  // Accept a field from AI research into the contact
  const handleAcceptField = useCallback(async (field, value) => {
    if (!value) return;
    const prev = field === 'name' ? prospect.name
      : field === 'email' ? prospect.email
      : field === 'phone' ? prospect.phone
      : prospect.data?.[field];
    setRevertValues(rv => ({ ...rv, [field]: prev }));
    setPendingUpdates(pu => ({ ...pu, [field]: value }));

    if (['name', 'email', 'phone'].includes(field)) {
      await updateContact.mutateAsync({ id: prospect.id, [field]: value });
    } else {
      // Update data JSONB field
      const newData = { ...prospect.data, [field]: value };
      await updateContact.mutateAsync({ id: prospect.id, data: newData });
    }
  }, [prospect, updateContact]);

  // Revert a field back to its original value
  const handleRevertField = useCallback(async (field) => {
    const prev = revertValues[field];
    if (['name', 'email', 'phone'].includes(field)) {
      await updateContact.mutateAsync({ id: prospect.id, [field]: prev || '' });
    } else {
      const newData = { ...prospect.data, [field]: prev || '' };
      await updateContact.mutateAsync({ id: prospect.id, data: newData });
    }
    setPendingUpdates(pu => { const n = { ...pu }; delete n[field]; return n; });
    setRevertValues(rv => { const n = { ...rv }; delete n[field]; return n; });
  }, [prospect, revertValues, updateContact]);

  // Helper: render an updatable field row
  const UpdateableField = ({ label, field, aiValue, currentValue, icon: FieldIcon }) => {
    if (!aiValue || aiValue === currentValue) return null;
    const accepted = pendingUpdates[field] !== undefined;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        {FieldIcon && <FieldIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mr: 0.5 }}>{label}:</Typography>
          {field === 'instagram'
            ? <Typography component="a" variant="body2" data-track="outreach.igHandle" href={`https://instagram.com/${aiValue}`} target="_blank" rel="noopener" sx={{ color: '#E1306C' }}>@{aiValue}</Typography>
            : field === 'website' || field === 'brokerage_url'
            ? <Typography component="a" variant="body2" data-track="outreach.website" href={aiValue.startsWith('http') ? aiValue : `https://${aiValue}`} target="_blank" rel="noopener" sx={{ color: 'primary.main' }}>{aiValue}</Typography>
            : aiValue}
        </Typography>
        {accepted ? (
          <Tooltip title="Revert to original">
            <IconButton size="small" onClick={() => handleRevertField(field)} sx={{ color: '#F59E0B' }}>
              <UndoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Update contact">
            <IconButton size="small" onClick={() => handleAcceptField(field, aiValue)} sx={{ color: '#22C55E' }}>
              <AcceptIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {accepted && <Chip label="Updated" size="small" sx={{ bgcolor: 'rgba(34,197,94,0.1)', color: '#22C55E', fontSize: '0.6rem', height: 18 }} />}
      </Box>
    );
  };

  // Research section renderers
  const renderContactVerification = (cu) => {
    if (!cu) return null;
    const fields = [
      { label: 'Name', field: 'name', value: cu.name, current: prospect.name, icon: PersonIcon },
      { label: 'Email', field: 'email', value: cu.email, current: prospect.email, icon: EmailIcon },
      { label: 'Alt Email', field: 'email_alt', value: cu.email_alt, current: null, icon: EmailIcon },
      { label: 'Instagram', field: 'instagram', value: cu.instagram, current: prospect.data?.ig, icon: InstagramIcon },
      { label: 'Website', field: 'website', value: cu.website, current: prospect.data?.website, icon: WebIcon },
      { label: 'Brokerage Profile', field: 'brokerage_url', value: cu.brokerage_url, current: null, icon: BusinessIcon },
    ].filter(f => f.value);
    if (!fields.length && !cu.phone_verification) return null;
    const phoneV = cu.phone_verification;
    const phoneVerified = phoneV?.verified === true || phoneV?.verified === 'true';
    return (
      <>
        {fields.map(f => (
          <UpdateableField key={f.field} label={f.label} field={f.field === 'instagram' ? 'ig' : f.field} aiValue={f.value} currentValue={f.current} icon={f.icon} />
        ))}
        {phoneV && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
            <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Phone:</Typography>
            <Chip
              label={phoneV.type === 'personal' ? 'Personal' : phoneV.type === 'office' ? 'Office' : 'Unknown'}
              size="small"
              sx={{
                height: 20, fontSize: '0.65rem', fontWeight: 700,
                bgcolor: phoneVerified
                  ? (phoneV.type === 'personal' ? '#22C55E20' : phoneV.type === 'office' ? '#F59E0B20' : '#94A3B820')
                  : '#EF444420',
                color: phoneVerified
                  ? (phoneV.type === 'personal' ? '#22C55E' : phoneV.type === 'office' ? '#F59E0B' : '#94A3B8')
                  : '#EF4444',
                border: '1px solid',
                borderColor: phoneVerified
                  ? (phoneV.type === 'personal' ? '#22C55E40' : phoneV.type === 'office' ? '#F59E0B40' : '#94A3B840')
                  : '#EF444440',
              }}
            />
            <Chip
              label={phoneVerified ? 'Verified' : 'Unverified'}
              size="small"
              icon={phoneVerified ? <CheckIcon sx={{ fontSize: '14px !important' }} /> : <WarningIcon sx={{ fontSize: '14px !important' }} />}
              sx={{ height: 20, fontSize: '0.6rem' }}
              variant="outlined"
              color={phoneVerified ? 'success' : 'warning'}
            />
            {phoneV.notes && <Typography variant="caption" color="text.secondary">{phoneV.notes}</Typography>}
          </Box>
        )}
      </>
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ fontWeight: 700 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AgentAvatar name={prospect.name} size={56} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {prospect.name}
              {prospect.data?.ig && (
                <Tooltip title={`@${prospect.data.ig}`}>
                  <IconButton size="small" component="a" data-track="outreach.igHandle" href={`https://instagram.com/${prospect.data.ig}`} target="_blank" rel="noopener" sx={{ color: '#E1306C' }}>
                    <InstagramIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">{prospect.company}</Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {/* Contact Info */}
          <Stack direction="row" spacing={3} flexWrap="wrap">
            {prospect.email && (
              <Typography variant="body2">
                <EmailIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {prospect.email}
              </Typography>
            )}
            {prospect.phone && (
              <Typography variant="body2">
                <PhoneIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {prospect.phone}
              </Typography>
            )}
            {website && (
              <Typography variant="body2" component="a" data-track="outreach.website" href={website.startsWith('http') ? website : `https://${website}`} target="_blank" rel="noopener" sx={{ color: 'primary.main' }}>
                <WebIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {website}
              </Typography>
            )}
          </Stack>

          {/* Status + Segment Controls */}
          <Stack direction="row" spacing={2} useFlexGap flexWrap={{ xs: 'wrap', md: 'nowrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select value={status} label="Status" onChange={e => onStatusChange(prospect, e.target.value)}>
                {OUTREACH_STATUSES.map(s => (
                  <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace(/_/g, ' ')}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Segment</InputLabel>
              <Select value={segment} label="Segment" onChange={e => onSegmentChange(prospect, e.target.value)}>
                {SEGMENTS.map(s => <MenuItem key={s} value={s}>{SEGMENT_LABELS[s]}</MenuItem>)}
              </Select>
            </FormControl>
            {/* Book / Convert quick actions */}
            {status !== 'booked' && status !== 'client' && status !== 'declined' && (
              <Button size="small" variant="outlined" startIcon={<CalendarIcon />}
                onClick={() => onBookConvert(prospect, 'booked')}
                sx={{ textTransform: 'none', fontSize: '0.75rem', borderColor: '#22D3EE', color: '#22D3EE' }}>
                Book Meeting (+50 XP)
              </Button>
            )}
            {status !== 'client' && (
              <Button size="small" variant="outlined" startIcon={<StarIcon />}
                onClick={() => onBookConvert(prospect, 'converted')}
                sx={{ textTransform: 'none', fontSize: '0.75rem', borderColor: '#A3E635', color: '#A3E635' }}>
                Convert to Client (+100 XP)
              </Button>
            )}
          </Stack>
          {experience && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                Licensed since {fmtLicensed(experience)}{experience.licenseId && ` (DRE #${experience.licenseId})`}{experience.lowerBound && ' · earliest record; may be earlier'}
              </Typography>
              {experience.years < 2 && !experience.lowerBound && segment !== 'new_agent' && (
                <Chip label="Set segment: New agent" size="small" variant="outlined" clickable
                  onClick={() => onSegmentChange(prospect, 'new_agent')}
                  sx={{ height: 20, fontSize: '0.65rem', borderColor: '#22C55E', color: '#22C55E' }} />
              )}
            </Box>
          )}

          {/* AI Research */}
          <Paper variant="outlined" sx={{ p: 2, borderColor: showResearch ? '#A855F7' : 'divider', transition: 'border-color 0.2s' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: showResearch ? 1.5 : 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AIIcon fontSize="small" sx={{ color: '#A855F7' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>AI Research</Typography>
                {researchedAt && !showResearch && (
                  <Chip label={`Last: ${fmtDate(researchedAt)}`} size="small" sx={{ fontSize: '0.6rem', height: 20 }} />
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {research && showResearch && (
                  <Tooltip title="Save as JPG">
                    <IconButton size="small" onClick={handleSaveScreenshot}>
                      <SaveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {research && (
                  <Tooltip title={`Re-research (deeper dive)${prospect.data?.ai_research_count > 1 ? ` — researched ${prospect.data.ai_research_count}x` : ''}`}>
                    <IconButton size="small" onClick={() => handleResearch(true)} disabled={researchLoading}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Button
                  size="small"
                  variant={research ? 'text' : 'contained'}
                  onClick={() => research ? setShowResearch(v => !v) : handleResearch()}
                  disabled={researchLoading}
                  startIcon={researchLoading ? <CircularProgress size={14} /> : <AIIcon fontSize="small" />}
                  sx={{
                    textTransform: 'none', fontSize: '0.75rem',
                    ...(!research && { bgcolor: '#A855F7', '&:hover': { bgcolor: '#9333EA' } }),
                  }}
                >
                  {researchLoading ? 'Researching…' : research ? (showResearch ? 'Hide' : 'Show') : 'Research Prospect'}
                </Button>
              </Box>
            </Box>

            <Collapse in={showResearch}>
              <Box ref={researchRef} sx={{ p: 0.5 }}>
              {/* Contact header for screenshot */}
              <Paper sx={{ p: 1.5, mb: 1.5, bgcolor: '#f8f9fa', border: 'none', boxShadow: 'none' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{prospect.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{prospect.company}</Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ fontSize: '0.82rem' }}>
                  {prospect.email && <Typography variant="body2"><EmailIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.3 }} />{prospect.email}</Typography>}
                  {prospect.phone && <Typography variant="body2"><PhoneIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.3 }} />{prospect.phone}</Typography>}
                  {prospect.data?.ig && <Typography variant="body2" sx={{ color: '#E1306C' }}><InstagramIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.3 }} />@{prospect.data.ig}</Typography>}
                  {website && <Typography variant="body2" sx={{ color: 'primary.main' }}><WebIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.3 }} />{website}</Typography>}
                </Stack>
              </Paper>
              {researchError && (
                <Alert severity="error" sx={{ mb: 1, py: 0 }}>{researchError}</Alert>
              )}

              {researchLoading && !research && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2, justifyContent: 'center' }}>
                  <CircularProgress size={20} sx={{ color: '#A855F7' }} />
                  <Typography variant="body2" color="text.secondary">Researching {prospect.name}…</Typography>
                </Box>
              )}

              {/* Structured JSON Research */}
              {research && isStructured && (
                <Stack spacing={1.5}>
                  {/* Section 1: Contact Verification */}
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(34,197,94,0.03)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <PersonIcon fontSize="small" sx={{ color: '#22C55E' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Contact Verification</Typography>
                    </Box>
                    {renderContactVerification(research.contact_updates)}
                  </Paper>

                  {/* Agent Classification */}
                  {research && isStructured && (() => {
                    const metrics = {
                      listings_12m: research.sales?.listings_12m ?? research.sales?.active_listings ?? null,
                      avg_price: typeof research.sales?.avg_price === 'number' ? research.sales.avg_price : null,
                      luxury_pct: typeof research.sales?.luxury_pct === 'number' ? research.sales.luxury_pct : null,
                      years_active: typeof research.agent_metrics?.years_active === 'number' ? research.agent_metrics.years_active : null,
                      social_score: typeof research.media?.social_score === 'number' ? research.media.social_score : null,
                      brand_score: typeof research.agent_metrics?.brand_score === 'number' ? research.agent_metrics.brand_score : null,
                    };
                    // Merge any manual overrides from contact data
                    const manual = prospect.data?.agent_metrics || {};
                    Object.keys(manual).forEach(k => { if (manual[k] != null) metrics[k] = manual[k]; });
                    const c = classifyAgent(metrics);
                    const scoreColor = c.normalizedScore >= 80 ? '#22C55E' : c.normalizedScore >= 60 ? '#3B82F6' : c.normalizedScore >= 35 ? '#F59E0B' : '#EF4444';
                    return (
                      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(168,85,247,0.04)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AIIcon fontSize="small" sx={{ color: '#A855F7' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Classification</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label={c.classification} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: `${scoreColor}15`, color: scoreColor, border: `1px solid ${scoreColor}40` }} />
                            <Chip label={`Score: ${c.normalizedScore}/100`} size="small" sx={{ fontWeight: 600, fontSize: '0.65rem' }} variant="outlined" />
                          </Box>
                        </Box>
                        {/* Score breakdown */}
                        <Stack spacing={0.5} sx={{ mb: 1 }}>
                          {[['Listings', 'listings', 25], ['Price Point', 'price', 25], ['Luxury %', 'luxury', 25], ['Experience', 'experience', 15], ['Brand', 'brand', 10]].map(([label, key, max]) => (
                            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" sx={{ width: 70, color: 'text.secondary', fontSize: '0.7rem' }}>{label}</Typography>
                              <LinearProgress variant="determinate" value={c.breakdown[key] != null ? (c.breakdown[key] / max) * 100 : 0}
                                sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.06)',
                                  '& .MuiLinearProgress-bar': { bgcolor: c.breakdown[key] != null ? scoreColor : '#CBD5E1' } }} />
                              <Typography variant="caption" sx={{ width: 35, textAlign: 'right', fontWeight: 600, fontSize: '0.7rem' }}>
                                {c.breakdown[key] != null ? `${c.breakdown[key]}/${max}` : '—'}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                        {c.missingFields.length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Missing: {c.missingFields.map(f => f.replace(/_/g, ' ')).join(', ')} — add manually for better accuracy
                          </Typography>
                        )}
                        {/* Segment recommendation */}
                        {c.segment !== segment && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" sx={{ flex: 1 }}>
                              Recommended: <SegmentChip segment={c.segment} />
                              {research.segment_reasoning && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{research.segment_reasoning}</Typography>}
                            </Typography>
                            <Button size="small" variant="outlined" startIcon={<AcceptIcon />}
                              onClick={() => onSegmentChange(prospect, c.segment)}
                              sx={{ textTransform: 'none', fontSize: '0.65rem' }}>Accept</Button>
                          </Box>
                        )}
                      </Paper>
                    );
                  })()}

                  {/* Section 2: Bio & Brand */}
                  {research.bio && (
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(59,130,246,0.03)' }}>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <BusinessIcon fontSize="small" sx={{ color: '#3B82F6' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Bio & Brand</Typography>
                      </Box>
                      <Stack spacing={0.3} sx={{ fontSize: '0.82rem' }}>
                        {research.bio.role && <Typography variant="body2"><b>Role:</b> {research.bio.role}</Typography>}
                        {research.bio.company && <Typography variant="body2"><b>Company:</b> {research.bio.company}</Typography>}
                        {research.bio.market_area && <Typography variant="body2"><b>Market:</b> {research.bio.market_area}</Typography>}
                        {research.bio.designations?.length > 0 && <Typography variant="body2"><b>Designations:</b> {research.bio.designations.join(', ')}</Typography>}
                        {research.bio.summary && <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>{research.bio.summary}</Typography>}
                        {research.bio.emphasis?.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                            {research.bio.emphasis.map((e, i) => <Chip key={i} label={e} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />)}
                          </Box>
                        )}
                      </Stack>
                    </Paper>
                  )}

                  {/* Section 3: Sales Activity */}
                  {research.sales && (
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(251,191,36,0.03)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <HomeIcon fontSize="small" sx={{ color: '#FBBF24' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Sales Activity</Typography>
                      </Box>
                      <Stack spacing={0.3} sx={{ fontSize: '0.82rem' }}>
                        <Stack direction="row" spacing={3}>
                          {research.sales.homes_sold_total != null && (
                            <Typography variant="body2"><b>Homes Sold:</b> {research.sales.homes_sold_total}</Typography>
                          )}
                          {research.sales.active_listings != null && (
                            <Typography variant="body2"><b>Active Listings:</b> {research.sales.active_listings}</Typography>
                          )}
                          {research.sales.avg_price_range && (
                            <Typography variant="body2"><b>Avg Range:</b> {research.sales.avg_price_range}</Typography>
                          )}
                        </Stack>
                        {research.sales.recent_listings?.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.5, display: 'block' }}>Recent Listings</Typography>
                            <Table size="small" sx={{ '& td, & th': { py: 0.3, px: 1, fontSize: '0.75rem', border: 'none' } }}>
                              <TableHead>
                                <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary' } }}>
                                  <TableCell>Address</TableCell>
                                  <TableCell>Price</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {research.sales.recent_listings.map((l, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{l.address}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{l.price}</TableCell>
                                    <TableCell>
                                      <Chip label={l.status} size="small" sx={{
                                        fontSize: '0.6rem', height: 18,
                                        bgcolor: l.status?.toLowerCase() === 'active' ? 'rgba(34,197,94,0.1)' : l.status?.toLowerCase() === 'sold' ? 'rgba(59,130,246,0.1)' : 'rgba(251,191,36,0.1)',
                                        color: l.status?.toLowerCase() === 'active' ? '#22C55E' : l.status?.toLowerCase() === 'sold' ? '#3B82F6' : '#FBBF24',
                                      }} />
                                    </TableCell>
                                    <TableCell>
                                      {l.source_url && (
                                        <Typography component="a" variant="caption" href={l.source_url} target="_blank" rel="noopener" sx={{ color: '#A855F7' }}>
                                          <LinkIcon sx={{ fontSize: 12 }} />
                                        </Typography>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        )}
                      </Stack>
                    </Paper>
                  )}

                  {/* Section 4: Media Presence */}
                  {research.media && (
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(168,85,247,0.03)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <VideoIcon fontSize="small" sx={{ color: '#A855F7' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Media Presence</Typography>
                      </Box>
                      <Stack spacing={0.5} sx={{ fontSize: '0.82rem' }}>
                        {research.media.ig_presence && (() => {
                          const igHandle = normalizeIgHandle(prospect.data?.ig || research?.contact_updates?.instagram);
                          return (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <InstagramIcon fontSize="small" sx={{ color: '#E1306C', mt: 0.2 }} />
                            <Box>
                              <Typography variant="body2">{research.media.ig_presence}</Typography>
                              {igHandle && (
                                <Typography
                                  component="a"
                                  variant="body2"
                                  data-track="outreach.igHandle"
                                  href={`https://instagram.com/${encodeURIComponent(igHandle)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ color: '#E1306C', display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}
                                >
                                  @{igHandle} <OpenIcon sx={{ fontSize: 12 }} />
                                </Typography>
                              )}
                              {research.media.ig_followers && <Typography variant="caption" color="text.secondary">{research.media.ig_followers} followers</Typography>}
                              {research.media.ig_content_style && <Typography variant="body2" sx={{ mt: 0.3 }}><b>Content Style:</b> {research.media.ig_content_style}</Typography>}
                            </Box>
                          </Box>
                          );
                        })()}
                        <Divider sx={{ my: 0.5 }} />
                        <Stack direction="row" spacing={2} flexWrap="wrap">
                          <Chip icon={<VideoIcon sx={{ fontSize: 14 }} />} label={research.media.has_video ? 'Has Video' : 'No Video'} size="small"
                            sx={{ fontSize: '0.65rem', height: 22, bgcolor: research.media.has_video ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: research.media.has_video ? '#22C55E' : '#EF4444' }} />
                          <Chip label={research.media.has_drone ? 'Has Drone/Aerial' : 'No Drone/Aerial'} size="small"
                            sx={{ fontSize: '0.65rem', height: 22, bgcolor: research.media.has_drone ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: research.media.has_drone ? '#22C55E' : '#EF4444' }} />
                          <Chip label={research.media.has_virtual_tours ? 'Has Virtual Tours' : 'No Virtual Tours'} size="small"
                            sx={{ fontSize: '0.65rem', height: 22, bgcolor: research.media.has_virtual_tours ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: research.media.has_virtual_tours ? '#22C55E' : '#EF4444' }} />
                        </Stack>
                        {research.media.youtube_channel && (
                          <Typography variant="body2">
                            <b>YouTube:</b>{' '}
                            <Typography component="a" variant="body2" href={research.media.youtube_channel} target="_blank" rel="noopener" sx={{ color: '#FF0000' }}>
                              {research.media.youtube_channel}
                            </Typography>
                          </Typography>
                        )}
                        {research.media.media_provider && (
                          <Typography variant="body2">
                            <b>Media Provider:</b> {research.media.media_provider}
                            {research.media.media_provider_source && <Typography component="span" variant="caption" color="text.secondary"> ({research.media.media_provider_source})</Typography>}
                          </Typography>
                        )}
                        {research.media.visible_gap && (
                          <Alert severity="info" sx={{ py: 0, mt: 0.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                            <b>Gap:</b> {research.media.visible_gap}
                          </Alert>
                        )}
                      </Stack>
                    </Paper>
                  )}

                  {/* Section 5: Outreach Intel */}
                  {research.outreach && (
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(236,72,153,0.03)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <OutreachIcon fontSize="small" sx={{ color: '#EC4899' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Outreach Intel</Typography>
                      </Box>
                      <Stack spacing={0.5} sx={{ fontSize: '0.82rem' }}>
                        {research.outreach.trigger && <Typography variant="body2"><b>Why Now:</b> {research.outreach.trigger}</Typography>}
                        {research.outreach.best_angle && <Typography variant="body2"><b>Best Angle:</b> {research.outreach.best_angle}</Typography>}
                        {research.outreach.personalization && (
                          <Paper sx={{ p: 1, bgcolor: 'rgba(236,72,153,0.06)', border: 'none', mt: 0.5 }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>"{research.outreach.personalization}"</Typography>
                          </Paper>
                        )}
                      </Stack>
                    </Paper>
                  )}

                  {/* Section 6: Sources */}
                  {research.sources?.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(107,114,128,0.03)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <LinkIcon fontSize="small" sx={{ color: '#6B7280' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Sources ({research.sources.length})</Typography>
                      </Box>
                      <Stack spacing={0.3}>
                        {research.sources.map((s, i) => (
                          <Typography key={i} variant="body2" sx={{ fontSize: '0.75rem' }}>
                            • <Typography component="a" variant="body2" href={s.url} target="_blank" rel="noopener" sx={{ color: '#A855F7', fontSize: '0.75rem' }}>
                              {s.title || s.url}
                            </Typography>
                          </Typography>
                        ))}
                      </Stack>
                    </Paper>
                  )}
                </Stack>
              )}

              {/* Legacy plain-text research (backward compat) */}
              {research && !isStructured && (
                <Box sx={{
                  bgcolor: 'rgba(168,85,247,0.04)', borderRadius: 1, p: 1.5,
                  '& a': { color: '#A855F7', textDecoration: 'underline', '&:hover': { color: '#9333EA' } },
                  whiteSpace: 'pre-wrap', fontSize: '0.82rem', lineHeight: 1.5,
                }}
                  dangerouslySetInnerHTML={{ __html: String(research)
                    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
                    .replace(/(^|\s)(https?:\/\/[^\s<]+)/g, '$1<a href="$2" target="_blank" rel="noopener noreferrer">$2</a>')
                  }}
                />
              )}

              {researchedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right' }}>
                  Researched {fmtDate(researchedAt)}
                </Typography>
              )}
              </Box>
            </Collapse>
          </Paper>

          {/* Cadence Timeline */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Cadence Timeline ({cadence.currentStep}/{cadence.cadenceSteps.length})
              </Typography>
              <Button size="small" variant="outlined" startIcon={<ReplyIcon />}
                onClick={onLogResponse}
                sx={{ textTransform: 'none', fontSize: '0.7rem', borderColor: '#4ADE80', color: '#4ADE80' }}>
                Log Reply
              </Button>
            </Box>

            {/* Timeline */}
            {(() => {
              // Build unified timeline: merge cadence steps + actual actions
              const startDate = cadence.startedAt ? new Date(cadence.startedAt) : null;
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              // Cadence step entries (with projected or actual dates)
              const stepEntries = cadence.cadenceSteps.map(step => {
                const done = cadence.completedSteps.some(s => s.day === step.day);
                const matchingAction = done ? actions.find(a => a.cadenceDay === step.day) : null;
                const actualDate = matchingAction?.createdAt ? new Date(matchingAction.createdAt) : null;
                const projectedDate = startDate ? new Date(startDate.getTime() + (step.day - 1) * 86400000) : null;
                const displayDate = actualDate || projectedDate;
                const isNext = cadence.nextStep?.day === step.day;
                const isPast = projectedDate && projectedDate < today && !done;
                return { type: 'step', step, done, isNext, isPast, matchingAction, displayDate, sortDate: displayDate };
              });

              // Reply entries (response actions not tied to cadence steps)
              const replyEntries = actions
                .filter(a => a.actionType === 'response')
                .map(a => ({
                  type: 'reply', action: a,
                  displayDate: a.createdAt ? new Date(a.createdAt) : null,
                  sortDate: a.createdAt ? new Date(a.createdAt) : null,
                }));

              // Merge and sort by date
              const timeline = [...stepEntries, ...replyEntries]
                .sort((a, b) => (a.sortDate || 0) - (b.sortDate || 0));

              return (
                <Stack spacing={0}>
                  {timeline.map((entry, idx) => {
                    const isLast = idx === timeline.length - 1;

                    if (entry.type === 'reply') {
                      const a = entry.action;
                      const sentimentColor = a.responseSentiment === 'positive' ? '#4ADE80' : a.responseSentiment === 'declined' ? '#F87171' : '#FBBF24';
                      const sentimentIcon = a.responseSentiment === 'positive' ? '🔥' : a.responseSentiment === 'declined' ? '✋' : '💬';
                      return (
                        <Box key={`reply-${a.id}`} sx={{ display: 'flex', gap: 1.5 }}>
                          {/* Timeline line + dot */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: sentimentColor, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.5rem' }}>
                              {sentimentIcon}
                            </Box>
                            {!isLast && <Box sx={{ width: 2, flex: 1, bgcolor: 'divider' }} />}
                          </Box>
                          {/* Content */}
                          <Box sx={{ pb: 1.5, flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ReplyIcon sx={{ fontSize: 14, color: sentimentColor }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, color: sentimentColor }}>
                                Client Reply
                              </Typography>
                              <Chip label={a.responseSentiment || 'neutral'} size="small"
                                sx={{ fontSize: '0.55rem', height: 18, bgcolor: `${sentimentColor}20`, color: sentimentColor, textTransform: 'capitalize' }} />
                              {a.channel && <Chip label={CHANNEL_LABELS[a.channel]} size="small" variant="outlined" sx={{ fontSize: '0.55rem', height: 18 }} />}
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                {entry.displayDate ? fmtDate(entry.displayDate.toISOString()) : ''}
                              </Typography>
                            </Box>
                            {a.notes && <Typography variant="body2" sx={{ mt: 0.3, fontSize: '0.78rem' }}>{a.notes}</Typography>}
                            {a.messageBody && (
                              <Box sx={{ mt: 0.5 }}>
                                <Chip label="View reply" size="small" clickable
                                  onClick={() => setExpandedLogMsg(prev => prev === a.id ? null : a.id)}
                                  sx={{ fontSize: '0.55rem', height: 18, bgcolor: 'rgba(59,130,246,0.1)', color: '#3B82F6' }} />
                                {expandedLogMsg === a.id && (
                                  <Box sx={{ mt: 0.5, p: 1.5, bgcolor: 'rgba(30,41,59,0.5)', borderRadius: 1, border: '1px solid rgba(148,163,184,0.1)' }}>
                                    <Typography variant="caption" sx={{ color: '#CBD5E1', whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
                                      "{a.messageBody}"
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                    }

                    // Cadence step entry
                    const { step, done, isNext, isPast, matchingAction } = entry;
                    const Icon = CHANNEL_ICONS[step.channel] || EmailIcon;
                    const hasMessage = matchingAction?.messageBody;
                    const dotColor = done ? '#4ADE80' : isNext ? '#3B82F6' : isPast ? '#F87171' : '#475569';

                    return (
                      <Box key={`step-${step.day}`} sx={{ display: 'flex', gap: 1.5 }}>
                        {/* Timeline line + dot */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
                          <Box sx={{
                            width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                            bgcolor: done ? dotColor : 'transparent',
                            border: done ? 'none' : `2px solid ${dotColor}`,
                          }} />
                          {!isLast && <Box sx={{ width: 2, flex: 1, bgcolor: 'divider' }} />}
                        </Box>
                        {/* Content */}
                        <Box sx={{ pb: 1.5, flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {done ? <CheckIcon sx={{ fontSize: 14, color: '#4ADE80' }} /> : <Icon sx={{ fontSize: 14, color: isNext ? '#3B82F6' : 'text.disabled' }} />}
                            <Typography variant="body2" sx={{
                              fontWeight: isNext ? 700 : done ? 500 : 400,
                              color: done ? 'text.primary' : isNext ? '#3B82F6' : 'text.secondary',
                              flex: 1,
                            }}>
                              Day {step.day}: {step.label}
                            </Typography>
                            <Chip label={CHANNEL_LABELS[step.channel]} size="small" variant="outlined" sx={{ fontSize: '0.55rem', height: 18 }} />
                            <Typography variant="caption" sx={{ color: '#FBBF24', fontWeight: 600, fontSize: '0.65rem' }}>
                              +{step.xp} XP
                            </Typography>
                            <Typography variant="caption" sx={{
                              color: done ? 'text.secondary' : isPast ? '#F87171' : 'text.disabled',
                              fontWeight: isPast ? 600 : 400,
                              fontSize: '0.7rem',
                            }}>
                              {entry.displayDate ? (done ? fmtDate(entry.displayDate.toISOString())
                                : isPast ? `Overdue (${fmtDate(entry.displayDate.toISOString())})`
                                : fmtDate(entry.displayDate.toISOString()))
                                : '—'}
                            </Typography>
                          </Box>

                          {/* Action buttons */}
                          {isNext && !done && (
                            <Button size="small" variant="outlined" onClick={() => onLogAction(step)}
                              sx={{ textTransform: 'none', fontSize: '0.7rem', mt: 0.5 }}>
                              Do It
                            </Button>
                          )}

                          {/* Expandable message */}
                          {hasMessage && (
                            <Box sx={{ mt: 0.5 }}>
                              <Chip label="View Message" size="small" clickable
                                onClick={() => setExpandedMsg(prev => prev === step.day ? null : step.day)}
                                sx={{ fontSize: '0.55rem', height: 18, bgcolor: 'rgba(59,130,246,0.1)', color: '#3B82F6' }}
                              />
                              {expandedMsg === step.day && (
                                <Box sx={{ mt: 0.5, p: 1.5, bgcolor: 'rgba(30,41,59,0.5)', borderRadius: 1, border: '1px solid rgba(148,163,184,0.1)' }}>
                                  {matchingAction.messageSubject && (
                                    <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mb: 0.5 }}>
                                      <strong>Subject:</strong> {matchingAction.messageSubject}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" sx={{ color: '#CBD5E1', whiteSpace: 'pre-wrap' }}>
                                    {matchingAction.messageBody}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              );
            })()}
          </Paper>

          {/* Cadence Completion */}
          {cadence.isComplete && status !== 'booked' && status !== 'client' && status !== 'declined' && (
            <Paper variant="outlined" sx={{ p: 2, borderColor: '#F59E0B', bgcolor: 'rgba(245,158,11,0.04)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrophyIcon sx={{ color: '#F59E0B' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#F59E0B' }}>
                  Cadence Complete — No Response
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                All {cadence.cadenceSteps.length} steps completed. This prospect hasn't responded. You can re-engage later or archive them.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={() => {
                  const reEngageStep = { day: 45, label: 'Re-Engage', channel: 'email', actionType: 'follow_up', xp: 8 };
                  onLogAction(reEngageStep);
                }} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                  Re-Engage
                </Button>
                <Button size="small" variant="outlined" color="error"
                  onClick={() => onStatusChange(prospect, 'declined')}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                  Archive (Declined)
                </Button>
              </Stack>
            </Paper>
          )}

          {/* Activity Log */}
          {actions.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Activity Log ({actions.length})</Typography>
              <Stack spacing={0.5}>
                {[...actions].reverse().map(a => {
                  const Icon = CHANNEL_ICONS[a.channel] || EmailIcon;
                  const hasMsg = a.messageBody;
                  const isReply = a.actionType === 'response';
                  const sentimentColor = a.responseSentiment === 'positive' ? '#4ADE80' : a.responseSentiment === 'declined' ? '#F87171' : '#FBBF24';
                  return (
                    <Box key={a.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3, cursor: hasMsg ? 'pointer' : 'default' }}
                        onClick={() => hasMsg && setExpandedLogMsg(prev => prev === a.id ? null : a.id)}
                      >
                        <Icon fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ flex: 1 }}>{a.notes || a.actionType?.replace(/_/g, ' ')}</Typography>
                        {isReply && a.responseSentiment && (
                          <Chip label={a.responseSentiment} size="small"
                            sx={{ fontSize: '0.55rem', height: 18, bgcolor: `${sentimentColor}20`, color: sentimentColor, textTransform: 'capitalize' }} />
                        )}
                        {hasMsg && <Chip label="msg" size="small" sx={{ fontSize: '0.55rem', height: 18, bgcolor: 'rgba(59,130,246,0.1)', color: '#3B82F6' }} />}
                        <Chip label={`+${getXP(a.actionType)}`} size="small" sx={{ bgcolor: 'rgba(251,191,36,0.1)', color: '#FBBF24', fontSize: '0.65rem', fontWeight: 700 }} />
                        <Typography variant="caption" color="text.secondary">{fmtDate(a.createdAt)}</Typography>
                      </Box>
                      {hasMsg && expandedLogMsg === a.id && (
                        <Box sx={{ ml: 4, mt: 0.5, mb: 1, p: 1.5, bgcolor: 'rgba(30,41,59,0.5)', borderRadius: 1, border: '1px solid rgba(148,163,184,0.1)' }}>
                          {a.messageSubject && (
                            <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mb: 0.5 }}>
                              <strong>Subject:</strong> {a.messageSubject}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: '#CBD5E1', whiteSpace: 'pre-wrap' }}>
                            {a.messageBody}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// Normalize an IG handle from various formats: @handle, handle, full URL
function normalizeIgHandle(val) {
  if (!val) return null;
  let h = String(val).trim();
  // Strip full URL
  const urlMatch = h.match(/instagram\.com\/([^/?#]+)/i);
  if (urlMatch) h = urlMatch[1];
  // Strip @ prefix
  h = h.replace(/^@/, '');
  return h || null;
}

// ── Instagram quick-add (when handle not found) ──────────────
function IgQuickAdd({ prospect, updateContact }) {
  const [igInput, setIgInput] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const handle = normalizeIgHandle(igInput);
    if (!handle || !updateContact) return;
    setSaving(true);
    try {
      await updateContact.mutateAsync({ id: prospect.id, data: { ...prospect.data, ig: handle } });
      setIgInput('');
    } catch (err) {
      console.error('Failed to save IG handle:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(245,158,11,0.04)', borderColor: 'rgba(245,158,11,0.3)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <WarningIcon sx={{ fontSize: 16, color: '#F59E0B' }} />
        <Typography variant="caption" sx={{ color: '#F59E0B', fontWeight: 600 }}>
          Instagram handle not found in research
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="@handle or profile URL"
          value={igInput}
          onChange={e => setIgInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><InstagramIcon sx={{ fontSize: 16, color: '#E1306C' }} /></InputAdornment>,
          }}
          sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
        />
        <Button
          size="small"
          variant="contained"
          disabled={!igInput.trim() || saving}
          onClick={handleSave}
          sx={{ textTransform: 'none', minWidth: 60 }}
        >
          {saving ? <CircularProgress size={14} /> : 'Save'}
        </Button>
      </Box>
    </Paper>
  );
}

// ── Log Action Dialog ────────────────────────────────────────
function LogActionDialog({ prospect, step, settings, onConfirm, onCancel, updateContact }) {
  const [channel, setChannel] = useState(step?.channel || 'email');
  const [notes, setNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [aiVariants, setAiVariants] = useState(null);
  const { isMobile } = useMobile();

  // Nature of outreach — defaults from cadence step
  const isTemplateChannel = TEMPLATE_CHANNELS.includes(channel);
  const defaultNature = useMemo(() => getNatureFromStep(step), [step]);
  const [nature, setNature] = useState(defaultNature || 'introduction');
  const availableNatures = useMemo(() => getNaturesForChannel(channel), [channel]);

  // Custom prompt (collapsible)
  const [customPrompt, setCustomPrompt] = useState('');
  const [showCustomPrompt, setShowCustomPrompt] = useState(false);

  // Style indicator
  const [styleCount, setStyleCount] = useState(null);

  // Find matching template
  const segment = prospect.data?.segment || 'unknown';
  const template = useMemo(() => {
    if (!isTemplateChannel) return null;
    return getTemplateForStep(step?.day, channel, segment)
      || TEMPLATE_LIST.find(t => t.cadenceDay === step?.day)
      || TEMPLATE_LIST.find(t => t.channel === channel);
  }, [step, channel, segment, isTemplateChannel]);

  const vars = useMemo(() => getProspectVars(prospect, settings), [prospect, settings]);
  const filledBody = useMemo(() => template ? fillTemplate(template.body, vars) : '', [template, vars]);
  const filledSubject = useMemo(() => template?.subject ? fillTemplate(template.subject, vars) : '', [template, vars]);

  // Editable message state — initialized from template
  const [editSubject, setEditSubject] = useState(filledSubject);
  const [editBody, setEditBody] = useState(filledBody);
  const [isEdited, setIsEdited] = useState(false);
  const [linkMenuAnchor, setLinkMenuAnchor] = useState(null);
  const bodyRef = useRef(null);

  // Links from settings
  const savedLinks = settings?.links || [];

  // Sync when template/channel changes (only if user hasn't manually edited)
  useEffect(() => {
    if (!isEdited) {
      setEditSubject(filledSubject);
      setEditBody(filledBody);
    }
  }, [filledSubject, filledBody, isEdited]);

  const handleBodyChange = (e) => { setEditBody(e.target.value); setIsEdited(true); };
  const handleSubjectChange = (e) => { setEditSubject(e.target.value); setIsEdited(true); };

  const handleResetTemplate = () => {
    setEditSubject(filledSubject);
    setEditBody(filledBody);
    setIsEdited(false);
  };

  const handleInsertLink = (link) => {
    const el = bodyRef.current;
    const pos = el?.selectionStart ?? editBody.length;
    const before = editBody.slice(0, pos);
    const after = editBody.slice(pos);
    const needsSpace = before.length > 0 && !before.endsWith('\n') && !before.endsWith(' ');
    const inserted = (needsSpace ? ' ' : '') + link.url;
    setEditBody(before + inserted + after);
    setIsEdited(true);
    setLinkMenuAnchor(null);
    // Restore cursor after the inserted link
    requestAnimationFrame(() => {
      if (el) {
        const newPos = pos + inserted.length;
        el.focus();
        el.setSelectionRange(newPos, newPos);
      }
    });
  };

  const handleCopy = () => {
    const text = editSubject ? `Subject: ${editSubject}\n\n${editBody}` : editBody;
    richCopy(text);
  };

  const handleMailto = async () => {
    if (!prospect.email) return;
    // Copy formatted body to clipboard (paragraph structure preserved, no font styles)
    await richCopy(editBody || '');
    const subject = encodeURIComponent(cleanCopy(editSubject || ''));
    window.open(`mailto:${prospect.email}?subject=${subject}`, '_self');
  };

  const [aiError, setAiError] = useState(null);
  const [generatingMode, setGeneratingMode] = useState(null); // 'personalize' | 'write'
  const [aiWasUsed, setAiWasUsed] = useState(false);

  // Ref to always have the latest values for auto-generate (avoids stale closures)
  const latestRef = useRef({ channel, nature, editBody, editSubject, filledBody, filledSubject, customPrompt, isEdited });
  latestRef.current = { channel, nature, editBody, editSubject, filledBody, filledSubject, customPrompt, isEdited };

  const handleAIGenerate = async (mode = 'personalize', { silent = false, overrides = {} } = {}) => {
    setGenerating(true);
    setGeneratingMode(mode);
    if (!silent) setAiVariants(null);
    setAiError(null);
    try {
      const cur = { ...latestRef.current, ...overrides };
      const payload = {
        contactId: prospect.id,
        templateBody: mode === 'personalize' ? (cur.editBody || cur.filledBody) : undefined,
        templateSubject: mode === 'personalize' ? (cur.editSubject || cur.filledSubject || undefined) : undefined,
        channel: cur.channel,
        cadenceDay: step?.day,
        stepLabel: step?.label,
        actionType: step?.actionType || 'follow_up',
        mode,
        nature: cur.nature,
        userPrompt: cur.customPrompt || undefined,
        variants: mode === 'write' && !silent ? 3 : undefined,
      };
      const result = await api.post('/ai/generate-message', payload);

      if (result.styleExamplesUsed != null) setStyleCount(result.styleExamplesUsed);

      if (result.variants) {
        setAiVariants(result.variants);
      } else {
        if (result.body) { setEditBody(result.body); setIsEdited(true); setAiWasUsed(true); }
        if (result.subject) { setEditSubject(result.subject); setIsEdited(true); }
      }
    } catch (err) {
      console.error('AI generate failed:', err);
      if (!silent) setAiError(err.message || 'AI generation failed');
    } finally {
      setGenerating(false);
      setGeneratingMode(null);
    }
  };

  // ── Auto-generate on mount, nature change, or channel switch back to template ──
  const hasAutoGenerated = useRef(false);
  useEffect(() => {
    if (!isTemplateChannel) return;
    // Skip if user has manually edited (except on first mount)
    if (latestRef.current.isEdited && hasAutoGenerated.current) return;

    hasAutoGenerated.current = true;
    const smartMode = prospect.data?.ai_research ? 'write' : 'personalize';
    handleAIGenerate(smartMode, { silent: true, overrides: { channel, nature } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nature, channel, isTemplateChannel]);

  const handlePickVariant = (variant) => {
    if (variant.body) { setEditBody(variant.body); setIsEdited(true); setAiWasUsed(true); }
    if (variant.subject) { setEditSubject(variant.subject); setIsEdited(true); }
    setAiVariants(null);
  };

  // AI research context for reference
  const research = prospect.data?.ai_research;
  const bestAngle = research?.outreach?.best_angle;
  const trigger = research?.outreach?.trigger;
  const gap = research?.media?.visible_gap;
  const personalization = research?.outreach?.personalization;

  return (
    <Dialog open onClose={onCancel} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Log Action: {step?.label || 'Outreach'}
        <Typography variant="body2" color="text.secondary">{prospect.name} — {prospect.company}</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Channel + Nature row */}
          <Stack direction="row" spacing={1.5}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Channel</InputLabel>
              <Select value={channel} label="Channel" onChange={e => {
                const ch = e.target.value;
                setChannel(ch);
                // Reset nature to valid option for new channel
                if (TEMPLATE_CHANNELS.includes(ch)) {
                  const valid = getNaturesForChannel(ch);
                  if (!valid.find(n => n.key === nature)) setNature(valid[0]?.key || 'introduction');
                }
              }}>
                {CHANNELS.map(c => <MenuItem key={c} value={c}>{CHANNEL_LABELS[c]}</MenuItem>)}
              </Select>
            </FormControl>

            {isTemplateChannel && (
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Nature</InputLabel>
                <Select value={nature} label="Nature" onChange={e => setNature(e.target.value)}>
                  {availableNatures.map(n => <MenuItem key={n.key} value={n.key}>{n.label}</MenuItem>)}
                </Select>
              </FormControl>
            )}

            {/* Style indicator */}
            {isTemplateChannel && styleCount != null && (
              <Tooltip title={styleCount >= 3 ? `AI using ${styleCount} of your past messages to match your style` : `Log ${3 - styleCount} more messages to enable style learning`}>
                <Chip
                  size="small"
                  icon={<AIIcon sx={{ fontSize: 14 }} />}
                  label={styleCount >= 3 ? `Style: ${styleCount} examples` : `Style: ${styleCount}/3`}
                  sx={{
                    alignSelf: 'center',
                    fontSize: '0.65rem',
                    bgcolor: styleCount >= 3 ? 'rgba(168,85,247,0.08)' : 'rgba(0,0,0,0.04)',
                    color: styleCount >= 3 ? '#A855F7' : 'text.secondary',
                  }}
                />
              </Tooltip>
            )}
          </Stack>

          {/* Instagram handle banner */}
          {channel === 'instagram_dm' && (() => {
            const igHandle = normalizeIgHandle(prospect.data?.ig || research?.contact_updates?.instagram);
            return igHandle ? (
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(225,48,108,0.04)', borderColor: 'rgba(225,48,108,0.3)', display: 'flex', alignItems: 'center', gap: 1 }}>
                <InstagramIcon sx={{ color: '#E1306C', fontSize: 20 }} />
                <Typography
                  component="a"
                  data-track="outreach.igHandle"
                  href={`https://instagram.com/${encodeURIComponent(igHandle)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ color: '#E1306C', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  @{igHandle}
                </Typography>
                <OpenIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              </Paper>
            ) : (
              <IgQuickAdd prospect={prospect} updateContact={updateContact} />
            );
          })()}

          {/* AI Research Intel — shown for template channels only */}
          {isTemplateChannel && (bestAngle || trigger || gap || personalization) && (
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(168,85,247,0.04)', borderColor: 'rgba(168,85,247,0.3)' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#A855F7', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <AIIcon sx={{ fontSize: 14 }} /> Research Intel
              </Typography>
              <Stack spacing={0.3}>
                {bestAngle && <Typography variant="caption">Best angle: {bestAngle}</Typography>}
                {trigger && <Typography variant="caption">Trigger: {trigger}</Typography>}
                {gap && <Typography variant="caption">Media gap: {gap}</Typography>}
                {personalization && <Typography variant="caption" sx={{ fontStyle: 'italic' }}>{personalization}</Typography>}
              </Stack>
            </Paper>
          )}

          {/* Custom Prompt — collapsible, template channels only */}
          {isTemplateChannel && (
            <Box>
              <Button
                size="small"
                onClick={() => setShowCustomPrompt(!showCustomPrompt)}
                startIcon={showCustomPrompt ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary', mb: showCustomPrompt ? 0.5 : 0 }}
              >
                Custom Instructions
              </Button>
              <Collapse in={showCustomPrompt}>
                <Stack spacing={1}>
                  <TextField
                    label="Tell the AI what to emphasize or adjust"
                    value={customPrompt}
                    onChange={e => setCustomPrompt(e.target.value)}
                    multiline
                    minRows={2}
                    maxRows={4}
                    size="small"
                    fullWidth
                    placeholder="e.g. Mention their recent $3M sale, keep it under 3 sentences, reference FPV specifically"
                    sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={generating ? <CircularProgress size={12} /> : <AIIcon />}
                      onClick={() => handleAIGenerate('write')}
                      disabled={generating || !customPrompt.trim() || !prospect.data?.ai_research}
                      sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#3B82F6', borderColor: '#3B82F6' }}
                    >
                      {generating ? 'Generating...' : 'Generate with Instructions'}
                    </Button>
                    <Button
                      size="small"
                      startIcon={generating ? <CircularProgress size={12} /> : <AIIcon />}
                      onClick={() => handleAIGenerate('personalize')}
                      disabled={generating || !customPrompt.trim()}
                      sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#A855F7' }}
                    >
                      Refine Current
                    </Button>
                  </Stack>
                </Stack>
              </Collapse>
            </Box>
          )}

          {/* Template section — only for template channels */}
          {isTemplateChannel && (template || generating) && (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: { xs: 'wrap', md: 'nowrap' }, gap: { xs: 0.5, md: 0 }, rowGap: { xs: 0.5, md: 0 } }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  Template: {template.label}
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  {isEdited && (
                    <Button size="small" startIcon={<UndoIcon />} onClick={handleResetTemplate}
                      sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
                      Reset
                    </Button>
                  )}
                  <Button size="small" startIcon={generating ? <CircularProgress size={12} /> : <AIIcon />}
                    onClick={() => handleAIGenerate('personalize')} disabled={generating}
                    sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#A855F7' }}>
                    {generating ? 'Generating...' : 'AI Personalize'}
                  </Button>
                  <Button size="small" startIcon={generating ? <CircularProgress size={12} /> : <AIIcon />}
                    onClick={() => handleAIGenerate('write')} disabled={generating || !prospect.data?.ai_research}
                    sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#3B82F6' }}>
                    AI Write
                  </Button>
                </Stack>
              </Box>

              {/* Variant picker */}
              {aiError && (
                <Alert severity="error" sx={{ mb: 1, py: 0 }}>{aiError}</Alert>
              )}
              {generating && generatingMode === 'write' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2, justifyContent: 'center' }}>
                  <CircularProgress size={18} />
                  <Typography variant="caption" color="text.secondary">Generating 3 approaches...</Typography>
                </Box>
              )}

              {aiVariants && !generating && (
                <Stack spacing={1} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#3B82F6' }}>
                    Pick an approach:
                  </Typography>
                  {aiVariants.map((v, i) => (
                    <Paper
                      key={v.approach || i}
                      variant="outlined"
                      onClick={() => handlePickVariant(v)}
                      sx={{
                        p: 1.5, cursor: 'pointer', borderColor: 'divider',
                        '&:hover': { borderColor: '#3B82F6', bgcolor: 'rgba(59,130,246,0.04)' },
                        transition: 'all 0.15s',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#3B82F6', mb: 0.5, display: 'block' }}>
                        {v.label}
                      </Typography>
                      {v.subject && (
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3 }}>
                          Subject: {v.subject}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'pre-line', lineHeight: 1.4 }}>
                        {v.body?.length > 200 ? v.body.slice(0, 200) + '…' : v.body}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )}

              {generating && !aiVariants ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4, gap: 1 }}>
                  <CircularProgress size={24} sx={{ color: '#A855F7' }} />
                  <Typography variant="caption" color="text.secondary">
                    {generatingMode === 'write' ? 'Writing message from research...' : 'Personalizing template...'}
                  </Typography>
                </Box>
              ) : (
                <>
                  {(channel === 'email') && (
                    <TextField
                      label="Subject"
                      value={editSubject}
                      onChange={handleSubjectChange}
                      size="small"
                      fullWidth
                      sx={{ mb: 1 }}
                    />
                  )}
                  <TextField
                    label="Message"
                    value={editBody}
                    onChange={handleBodyChange}
                    inputRef={bodyRef}
                    multiline
                    minRows={4}
                    maxRows={12}
                    size="small"
                    fullWidth
                    sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', lineHeight: 1.5 } }}
                  />
                </>
              )}
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button size="small" startIcon={<CopyIcon />} onClick={handleCopy} sx={{ textTransform: 'none' }}>
                  Copy
                </Button>
                {savedLinks.length > 0 && (
                  <>
                    <Button size="small" startIcon={<LinkIcon />}
                      onClick={e => setLinkMenuAnchor(e.currentTarget)}
                      sx={{ textTransform: 'none' }}>
                      Insert Link
                    </Button>
                    <Menu anchorEl={linkMenuAnchor} open={Boolean(linkMenuAnchor)}
                      onClose={() => setLinkMenuAnchor(null)}
                      slotProps={{ paper: { sx: { maxHeight: 300, minWidth: 220 } } }}>
                      {savedLinks.map(link => (
                        <MenuItem key={link.id} onClick={() => handleInsertLink(link)}
                          sx={{ fontSize: '0.8rem', py: 0.8 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{link.label}</Typography>
                            {link.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                                {link.description}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.6rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 250, opacity: 0.6 }}>
                              {link.url}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
                {prospect.email && channel === 'email' && (
                  <Button size="small" startIcon={<EmailIcon />} onClick={handleMailto} sx={{ textTransform: 'none' }}>
                    Open in Email
                  </Button>
                )}
              </Stack>
            </Paper>
          )}

          {/* Notes field — required for phone/in_person, optional for template channels */}
          <TextField
            label={isTemplateChannel ? 'Notes (optional)' : 'Notes'}
            multiline
            rows={isTemplateChannel ? 2 : 4}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            size="small"
            fullWidth
            required={!isTemplateChannel}
            placeholder={!isTemplateChannel ? 'Describe the conversation, key points discussed, next steps...' : ''}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!isTemplateChannel && !notes.trim()}
          onClick={() => onConfirm({
            contactId: prospect.id,
            actionType: step?.actionType || 'follow_up',
            channel,
            cadenceDay: step?.day || null,
            templateUsed: template?.key || null,
            notes: notes || `${step?.label || 'Action'} via ${CHANNEL_LABELS[channel]}`,
            messageSubject: isTemplateChannel ? (editSubject || null) : null,
            messageBody: isTemplateChannel ? (editBody || null) : null,
            nature: isTemplateChannel ? nature : null,
            customPrompt: customPrompt || null,
            aiUsed: aiWasUsed,
          })}
          startIcon={<CheckIcon />}
        >
          Log Action (+{getXP(step?.actionType || 'follow_up')} XP)
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════
// TEMPLATES (shown in dialog)
// ═══════════════════════════════════════════════════════════
function TemplatesTab({ settings }) {
  const [copied, setCopied] = useState(null);
  const [segmentView, setSegmentView] = useState('top_producer');

  const SEGMENT_ORDER = [
    { key: 'top_producer', label: 'Top Producer', color: '#EF4444' },
    { key: 'luxury', label: 'Luxury', color: '#A855F7' },
    { key: 'mid_volume', label: 'Mid-Volume', color: '#3B82F6' },
    { key: 'new_agent', label: 'New Agent', color: '#22C55E' },
    { key: 'generic', label: 'Generic', color: '#94A3B8' },
  ];

  const grouped = useMemo(() => {
    const groups = {};
    for (const seg of SEGMENT_ORDER) groups[seg.key] = [];
    for (const t of TEMPLATE_LIST) {
      const seg = t.segment || 'generic';
      if (groups[seg]) groups[seg].push(t);
      else groups.generic.push(t);
    }
    // Sort each group by cadence day
    for (const seg of Object.keys(groups)) {
      groups[seg].sort((a, b) => (a.cadenceDay || 0) - (b.cadenceDay || 0));
    }
    return groups;
  }, []);

  const currentSegment = SEGMENT_ORDER.find(s => s.key === segmentView);
  const templates = grouped[segmentView] || [];

  const exampleVars = {
    first_name: 'Sarah',
    office: 'Village Properties',
    listing_address: '123 Montecito Lane',
    neighborhood: 'Montecito',
    observation: 'your Montecito listings have great curb appeal that would look incredible from the air',
    portfolio_link: settings?.portfolioLink || 'https://rotoflight.com/portfolio',
    video_link: settings?.videoLink || 'https://rotoflight.com/reel',
    your_name: settings?.yourName || 'Jon',
    phone: settings?.companyPhone || '(805) 000-0000',
    website: settings?.companyWeb || 'rotoflight.com',
  };

  return (
    <Box>
      {/* Segment tabs */}
      <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
        {SEGMENT_ORDER.map(seg => (
          <Chip
            key={seg.key}
            label={`${seg.label} (${(grouped[seg.key] || []).length})`}
            onClick={() => setSegmentView(seg.key)}
            size="small"
            sx={{
              fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
              bgcolor: segmentView === seg.key ? `${seg.color}20` : 'transparent',
              color: segmentView === seg.key ? seg.color : 'text.secondary',
              border: `1px solid ${segmentView === seg.key ? seg.color : 'transparent'}`,
              '&:hover': { bgcolor: `${seg.color}10` },
            }}
          />
        ))}
      </Stack>

      {/* Cadence flow header */}
      {currentSegment && segmentView !== 'generic' && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: `${currentSegment.color}08`, borderColor: `${currentSegment.color}30` }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: currentSegment.color, display: 'block', mb: 0.5 }}>
            {currentSegment.label} Cadence — {templates.length} steps
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
            {templates.map((t, i) => {
              const Icon = CHANNEL_ICONS[t.channel] || EmailIcon;
              return (
                <Chip key={t.key} size="small" variant="outlined"
                  icon={<Icon sx={{ fontSize: '14px !important' }} />}
                  label={`Day ${t.cadenceDay}`}
                  sx={{ fontSize: '0.65rem', height: 24, borderColor: `${currentSegment.color}40`, color: currentSegment.color }}
                />
              );
            })}
          </Stack>
        </Paper>
      )}

      {/* Template cards */}
      <Stack spacing={2}>
        {templates.map(t => {
          const filled = fillTemplate(t.body, exampleVars);
          const filledSubject = t.subject ? fillTemplate(t.subject, exampleVars) : null;
          const Icon = CHANNEL_ICONS[t.channel] || EmailIcon;

          return (
            <Paper key={t.key} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon fontSize="small" color="action" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t.label}</Typography>
                  <Chip label={CHANNEL_LABELS[t.channel]} size="small" variant="outlined" sx={{ fontSize: '0.6rem' }} />
                  {t.cadenceDay && <Chip label={`Day ${t.cadenceDay}`} size="small" sx={{ fontSize: '0.6rem', bgcolor: `${currentSegment?.color || '#3B82F6'}15`, color: currentSegment?.color || '#3B82F6' }} />}
                </Box>
                <Tooltip title={copied === t.key ? 'Copied!' : 'Copy template'}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const text = filledSubject ? `Subject: ${filledSubject}\n\n${filled}` : filled;
                      richCopy(text);
                      setCopied(t.key);
                      setTimeout(() => setCopied(null), 2000);
                    }}
                  >
                    {copied === t.key ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
              {filledSubject && (
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Subject: {filledSubject}</Typography>
              )}
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem', lineHeight: 1.5, color: 'text.secondary' }}>
                {filled}
              </Typography>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════
// OUTREACH SETTINGS DIALOG
// ═══════════════════════════════════════════════════════════

function OutreachSettingsDialog({ settings, onClose }) {
  const updateSettings = useUpdateSettings();
  const { isMobile } = useMobile();
  const [saving, setSaving] = useState(false);

  // Link directory
  const [links, setLinks] = useState(settings?.data?.links || []);
  const [newLabel, setNewLabel] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [editLink, setEditLink] = useState({ label: '', url: '', description: '' });

  const handleAddLink = () => {
    if (!newLabel.trim() || !newUrl.trim()) return;
    const url = newUrl.startsWith('http') ? newUrl : `https://${newUrl}`;
    setLinks(prev => [...prev, { label: newLabel.trim(), url, description: newDesc.trim(), id: Date.now().toString(36) }]);
    setNewLabel('');
    setNewUrl('');
    setNewDesc('');
  };

  const startEditLink = (link) => {
    setEditingLinkId(link.id);
    setEditLink({ label: link.label, url: link.url, description: link.description || '' });
  };

  const saveEditLink = () => {
    if (!editLink.label.trim() || !editLink.url.trim()) return;
    const url = editLink.url.startsWith('http') ? editLink.url : `https://${editLink.url}`;
    setLinks(prev => prev.map(l => l.id === editingLinkId
      ? { ...l, label: editLink.label.trim(), url, description: editLink.description.trim() }
      : l
    ));
    setEditingLinkId(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings.mutateAsync({
        ...settings,
        data: { ...settings.data, links },
      });
      onClose();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Link Directory
        <Typography variant="body2" color="text.secondary">Save links to quickly insert into outreach messages</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Add new */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1}>
                <TextField label="Label" value={newLabel} onChange={e => setNewLabel(e.target.value)}
                  size="small" sx={{ flex: 1 }} placeholder="FPV Reel #1" />
                <TextField label="URL" value={newUrl} onChange={e => setNewUrl(e.target.value)}
                  size="small" sx={{ flex: 1 }} placeholder="https://..." />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="flex-end">
                <TextField label="Description (optional)" value={newDesc} onChange={e => setNewDesc(e.target.value)}
                  size="small" sx={{ flex: 1 }} placeholder="30-sec FPV walkthrough of a Montecito listing"
                  onKeyDown={e => e.key === 'Enter' && handleAddLink()} />
                <Button variant="contained" size="small" onClick={handleAddLink}
                  sx={{ textTransform: 'none', minWidth: 60 }}>Add</Button>
              </Stack>
            </Stack>
          </Paper>

          {/* Link list */}
          {links.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No links saved yet. Add your first link above.
            </Typography>
          ) : (
            <Stack spacing={0}>
              {links.map(link => (
                <Box key={link.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1, px: 1.5,
                  borderBottom: '1px solid', borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' }, '&:hover .link-actions': { opacity: 1 } }}>
                  {editingLinkId === link.id ? (
                    <Stack spacing={1} sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={1}>
                        <TextField label="Label" value={editLink.label} onChange={e => setEditLink(p => ({ ...p, label: e.target.value }))}
                          size="small" sx={{ flex: 1 }} />
                        <TextField label="URL" value={editLink.url} onChange={e => setEditLink(p => ({ ...p, url: e.target.value }))}
                          size="small" sx={{ flex: 1 }} />
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField label="Description" value={editLink.description} onChange={e => setEditLink(p => ({ ...p, description: e.target.value }))}
                          size="small" sx={{ flex: 1 }}
                          onKeyDown={e => e.key === 'Enter' && saveEditLink()} />
                        <Button size="small" variant="contained" onClick={saveEditLink}
                          sx={{ textTransform: 'none', minWidth: 50, fontSize: '0.75rem' }}>Save</Button>
                        <Button size="small" onClick={() => setEditingLinkId(null)}
                          sx={{ textTransform: 'none', minWidth: 50, fontSize: '0.75rem' }}>Cancel</Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{link.label}</Typography>
                          <Typography variant="caption" component="a" href={link.url} target="_blank" rel="noopener noreferrer"
                            sx={{ color: 'primary.main', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}>
                            {link.url}
                          </Typography>
                        </Box>
                        {link.description && (
                          <Typography variant="caption" color="text.secondary">{link.description}</Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0} className="link-actions" sx={{ opacity: { xs: 1, md: 0 }, transition: 'opacity 0.15s', mt: 0.3 }}>
                        <IconButton size="small" onClick={() => startEditLink(link)}
                          sx={{ color: 'text.secondary' }}>
                          <SettingsIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                        <IconButton size="small"
                          onClick={() => setLinks(prev => prev.filter(l => l.id !== link.id))}
                          sx={{ color: 'error.main' }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Stack>
                    </>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
